import React, { useState, useEffect, useContext, useRef } from "react";
import CancellationPolicyDisplay from "../CancellationPolicyDisplay";
import {
  BookingStatusUpdateEmailBody,
  getBookingByID,
  getBookingOnce,
  sendWaiverBody,
  sendEmail,
  getCurrentUserDetails,
  getDisputeDetailsById,
  getInquiryDataById,
  getListingByID,
  getPaymentById,
  getTipPaymentsByBookingId,
  getReviewsByListingId,
  getUserByID,
  updateBookingStatus,
  updateDisputeStatus,
  updateHeldPaymentStatus,
  BookingCompletedWithDisputeRenterEmailBody,
  BookingCompletedWithDisputeOwnerEmailBody,
  getPendingPaymentById,
  sendSMS,
  tripStatusUpdateEmailBody,
  getWaiverDescriptionByName,
  getRequiredPassengerWaivers,
  getFilledPassengerWaivers,
  getSignedPassengerWaiverCount,
  arePassengerWaiversComplete,
  getCommissionRateByReferralCode,
  getOwnerMeta,
  isOffPlatformDeposit,
  recordOffPlatformDeposit,
  getInspectionIssueItemsFromBooking,
  getDamageClaimChargeLines,
  getInspectionIssueCategory,
  requiresRenterApprovalForInspection,
  allowsDirectChargeFromInspection,
  formatRenterInspectionIssuePhrase,
  formatRenterInspectionChargeLabel,
  isInspectionChargeAnswer,
  sendBookingChatMessage,
  formatUploadedAssetUrl,
} from "../utils";
import {
  notifyOwnerTripStarted,
  notifyRenterTripIssue,
  notifyOwnerReviewReceived,
  notifyOwnerDamageClaimApproved,
} from "../utils/userNotifications";
import moment from "moment-timezone";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { Alert } from "@mui/material";
import { Button } from "react-bootstrap";
import { Form, Col, Modal, Row, InputGroup, Dropdown } from "react-bootstrap";
import customParseFormat from 'dayjs/plugin/customParseFormat';
import CurrencyInput from "react-currency-input-field";
import TextField from "@mui/material/TextField";
import { useAuth } from "../../contexts/AuthContext";
import {
  getDatabase,
  ref,
  get,
  push,
  set,
  update,
  serverTimestamp,
  onValue,
} from "firebase/database";
import { FaRegCopy } from "react-icons/fa";
import {
  RiDeleteBin4Line,
  RiDeleteBin6Line,
  RiEditLine,
  RiMessage2Line,
  RiMessageLine,
  RiMailLine,
  RiMailSendLine,
  RiQuestionLine,
  RiDeleteBinLine,
  RiDeleteBin2Line,
  RiDeleteBin3Line,
  RiDeleteBin5Line,
  RiDeleteBinFill,
  RiDeleteBin2Fill,
  RiDeleteBin3Fill,
  RiDeleteBin5Fill,
  RiDeleteBin6Fill,
  RiDownloadLine,
} from "react-icons/ri";

import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import ReceiptTemplate from "./ReceiptTemplate";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";
import { ThreeDots } from "react-loader-spinner";
import { toast } from "react-toastify";
import EditBookingModal from "./EditBookingModal";
import {
  formatPassengerSummary,
  getPassengerCounts,
} from "../utils/passengerCounts";

dayjs.extend(customParseFormat);

// Utility function to format currency values to 2 decimal places
const formatCurrency = (value) => {
  if (value === null || value === undefined) return "";
  return Number(value).toFixed(2);
};

const isRenterDrivingTrip = (boatDetails, inquiryData) => {
  const offer = inquiryData?.offerData;
  if (offer?.captainCheckbox) return false;
  if (
    boatDetails?.boatType === "Fishing Guide" ||
    boatDetails?.boatType === "Fishing Charter"
  ) {
    return false;
  }
  if (boatDetails?.boatCaptain === true) return false;
  return true;
};

const isCaptainDrivenTrip = (boatDetails, inquiryData) =>
  !isRenterDrivingTrip(boatDetails, inquiryData);

const hasRenterAcceptedOffer = (bookingData, inquiryData) => {
  if (
    bookingData?.instantBook ||
    bookingData?.packageBooking ||
    !bookingData?.inquiryId
  ) {
    return true;
  }

  if (String(inquiryData?.status || "").toLowerCase() === "accepted") {
    return true;
  }

  // Accept Offer in inbox writes the quoted price onto the booking.
  return Number(bookingData?.priceOffered) > 0;
};

const getCaptainStartTripGuidanceMessage = (boatDetails) => {
  if (
    boatDetails?.boatType === "Fishing Guide" ||
    boatDetails?.boatType === "Fishing Charter"
  ) {
    return "Your client's waivers are in — nice! Hit Start Trip to officially kick off the charter. Make sure everyone is aboard, briefed on safety, and has signed their waivers before you cast off. When you're back, End Trip from this same booking to wrap up and get paid.";
  }
  return "Your renter's waivers are in — you're good to go! Hit Start Trip to officially begin the trip. Walk them through safety gear and boat basics before you head out. When you return, End Trip from this booking to finish up and release any holds.";
};

const isTripEndedStatus = (status) => {
  const normalized = status?.toLowerCase();
  return (
    normalized === "trip returned" ||
    normalized === "trip completed" ||
    normalized === "trip concluded"
  );
};

const isInspectionAllGood = (booking) => {
  if (booking?.tripData?.inspectionAllGood === true) {
    return true;
  }
  if (booking?.tripData?.inspectionAllGood === false) {
    return false;
  }

  const checklist =
    booking?.tripData?.waivers?.["Boat Owner Equipment Checklist"];
  if (!checklist) {
    // Captain-driven trips skip end-of-trip inspection; no checklist means no reported issues.
    return true;
  }

  const entries = Object.values(checklist);
  if (!entries.length) {
    return true;
  }

  const latestEntry = entries[entries.length - 1];
  const questions = latestEntry?.questions || [];

  if (!questions.length) {
    return true;
  }

  return !questions.some((question) =>
    isInspectionChargeAnswer(question.answer)
  );
};

const canOwnerSubmitDamageClaim = (booking) => {
  const status = booking?.damageClaim?.status;
  return !status || status === "rejected";
};

const isDamageClaimResolved = (booking) =>
  ["approved", "charged"].includes(booking?.damageClaim?.status);

const hasTripInspectionIssue = (booking) => {
  if (!booking) {
    return false;
  }

  if (isDamageClaimResolved(booking)) {
    return false;
  }

  if (booking.damageClaim?.status === "pending_renter_approval") {
    return true;
  }

  if (booking.damageClaim?.status === "rejected") {
    return true;
  }

  if (booking.status === "Disputed") {
    return true;
  }

  if (booking.tripData?.inspectionAllGood === false) {
    return true;
  }

  if (booking.tripData?.inspectionAllGood === true) {
    return false;
  }

  return !isInspectionAllGood(booking);
};

const getWaiversCompleteGuidanceTitle = (boatDetails, inquiryData) => {
  if (isRenterDrivingTrip(boatDetails, inquiryData)) {
    return "Passenger waivers are complete!";
  }
  return "Waivers complete!";
};

const getWaiversCompleteGuidanceMessage = (boatDetails, inquiryData) => {
  if (isRenterDrivingTrip(boatDetails, inquiryData)) {
    return 'Nothing left to do until you arrive at the pickup location and meet the boat owner. Once there, the boat driver will click on the booking on the calendar and click "Start Trip".';
  }
  return "All required waivers are complete — you're good to go! Show up at the agreed time and location on trip day. Your captain will start the trip from their end — just relax and enjoy!";
};

// Helper function to calculate renter fee (straight 5%, no cap)
// For fishing charters (boatCaptain === true): charge 5% platform fee as renter fee
// For regular rentals (boatCaptain === false): charge 5% renter fee
// Must match PaymentForm.js logic exactly
const calculateRenterFee = (amount, listingData = null) => {
  return 0.05 * amount;
};

const listingReviewCategories = [
  "Listing Accuracy",
  "Itinerary & Experience",
  "Vessel & Equipment",
  "Departure & Return",
  "Value",
  "Communication",
];

function formatDateTime(dateTimeString) {
  if (!dateTimeString) return "";
  const formats = [
    "dddd, MMMM D, YYYY h:mm A",
    "dddd, MMMM D, YYYY hh:mm a",
    "dddd, MMMM D, YYYY h:mm:a",
    "dddd, MMMM D, YYYY hh:mm:a",
    "MMMM D, YYYY h:mm A",
    "MMMM D, YYYY hh:mm a",
    "MMMM D, YYYY h:mm:a",
    "MMMM D, YYYY hh:mm:a",
    "dddd, MMMM D, YYYY HH:mm",
    "MMMM D, YYYY HH:mm",
    "YYYY-MM-DD HH:mm",
    "YYYY-MM-DD h:mm A"
  ];
  const m = moment(dateTimeString, formats, true);
  if (m.isValid()) {
    return m.format("MMMM D, YYYY, h:mm A");
  }
  const fallback = moment(dateTimeString);
  if (fallback.isValid()) {
    return fallback.format("MMMM D, YYYY, h:mm A");
  }
  const dateObj = new Date(dateTimeString);
  if (!isNaN(dateObj.getTime())) {
    const options = {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    };
    return dateObj.toLocaleString("en-US", options);
  }
  return String(dateTimeString);
}

const calculateReturnDateTime = (
  offeredDate,
  offeredTime,
  duration,
  overnight = null
) => {
  if (!offeredDate || !offeredTime) return null;
  if (!duration && !overnight) return null;

  const dateTimeString = `${offeredDate} ${offeredTime}`;
  const formats = [
    "dddd, MMMM D, YYYY h:mm A",
    "dddd, MMMM D, YYYY hh:mm a",
    "dddd, MMMM D, YYYY h:mm:a",
    "dddd, MMMM D, YYYY hh:mm:a",
    "MMMM D, YYYY h:mm A",
    "MMMM D, YYYY hh:mm a",
    "MMMM D, YYYY h:mm:a",
    "MMMM D, YYYY hh:mm:a",
    "dddd, MMMM D, YYYY HH:mm",
    "MMMM D, YYYY HH:mm",
    "YYYY-MM-DD HH:mm",
    "YYYY-MM-DD h:mm A"
  ];
  const arrivalMoment = moment(dateTimeString, formats, true);
  const finalMoment = arrivalMoment.isValid() ? arrivalMoment : moment(dateTimeString);

  if (!finalMoment.isValid()) return null;

  let returnDate = finalMoment.clone();

  if (overnight) {
    const days = Number(overnight);
    returnDate = returnDate.add(days, "days");
  } else {
    returnDate = returnDate.add(Number(duration), "hours");
  }

  return {
    date: returnDate.format("dddd, MMMM D, YYYY"),
    time: returnDate.format("h:mm A"),
    fullDateTime: returnDate.format("MMMM D, YYYY, h:mm A"),
  };
};

const extractPassengerNamesFromBooking = (bookingDetails) => {
  const extractedNames = [];
  if (!bookingDetails?.tripData?.waivers) {
    return extractedNames;
  }

  const adultWaivers = bookingDetails.tripData.waivers["Adult Waiver"];
  const minorWaivers = bookingDetails.tripData.waivers["Minor Waiver"];
  const nameFields = ["first name", "middle name", "last name"];

  const extractName = (waiver) => {
    const questions = waiver.questions || [];
    return questions
      .filter((q) => nameFields.includes((q.question || "").toLowerCase()))
      .map((q) => (q.answer || "").trim())
      .filter(Boolean)
      .join(" ");
  };

  if (adultWaivers) {
    Object.values(adultWaivers).forEach((waiver) => {
      const name = extractName(waiver);
      if (name) {
        extractedNames.push({
          name,
          type: "Adult",
          waiverData: waiver.questions || [],
          signedAt: waiver.signedAt || null,
        });
      }
    });
  }

  if (minorWaivers) {
    Object.values(minorWaivers).forEach((waiver) => {
      const name = extractName(waiver);
      if (name) {
        extractedNames.push({
          name,
          type: "Minor",
          waiverData: waiver.questions || [],
          signedAt: waiver.signedAt || null,
        });
      }
    });
  }

  return extractedNames;
};

export default function Event() {
  const customStyles = `
    .event-text p{
    font-size: 14px;
}`
  const { dynamicId } = useParams();
  const location = useLocation();
  const Navigate = useNavigate();
  const auth = useAuth();
  const { currentUser } = auth;

  const [offerStartDateTime, setOfferStartDateTime] = useState(dayjs());

  const [waiverType, setWaiverType] = useState("");
  const [bookingData, setBookingData] = useState(null);
  const [timeStamp, setTimeStamp] = useState(null);
  const [ownerData, setOwnerData] = useState(null);
  const [ownerPaidBookings, setOwnerPaidBookings] = useState(null);
  const [ownerCommissionRate, setOwnerCommissionRate] = useState(0.1);
  const [renterData, setRenterData] = useState(null);
  const [inquiryData, setInquiryData] = useState(null);
  const [listingData, setListingData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [dropOffLocation, setDropOffLocation] = useState("");
  const [pickUpLocation, setPickUpLocation] = useState("");
  const [disputeAmount, setDisputeAmount] = useState(0);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const holdChargeCreditRef = useRef("");
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [tipPayments, setTipPayments] = useState([]);
  const [listingReviews, setListingReviews] = useState([]);
  const [showListingReviewModal, setShowListingReviewModal] = useState(false);
  const [showSubmittedReviewModal, setShowSubmittedReviewModal] = useState(false);
  const [listingReviewFormData, setListingReviewFormData] = useState({
    review: "",
    rating: 0,
    "Listing Accuracy": 0,
    "Itinerary & Experience": 0,
    "Vessel & Equipment": 0,
    "Departure & Return": 0,
    Value: 0,
    Communication: 0,
  });

  const [removeModal, setRemoveModal] = useState(false);
  const [removeBookingLoading, setRemoveBookingLoading] = useState(false);

  const [pendingPaymentDetails, setPendingPaymentDetails] = useState(null);

  const [disputeDetails, setDisputeDetails] = useState([]);

  const [isDisuputeModalOpen, setDisputeModalOpen] = useState(false);
  const [waiverReceiverEmail, setWaiverReceiverEmail] = useState("");

  const [disputeReason, setDisputeReason] = useState("");
  const [damageClaimReceiptUrls, setDamageClaimReceiptUrls] = useState([]);
  const [damageClaimSubmitting, setDamageClaimSubmitting] = useState(false);
  const [boatDetails, setBoatDetails] = useState({});
  const [passengerNames, setPassengerNames] = useState([]);
  const [selectedWaiver, setSelectedWaiver] = useState(null);
  const [showWaiverModal, setShowWaiverModal] = useState(false);
  const [waiverDescription, setWaiverDescription] = useState("");
  const [waiverDescLoading, setWaiverDescLoading] = useState(false);

  // Download Receipt states and reference
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showReceiptPreview, setShowReceiptPreview] = useState(false);
  const [receiptViewRole, setReceiptViewRole] = useState(null);
  const receiptRef = React.useRef(null);
  const totalTipAmount = tipPayments.reduce(
    (total, payment) => total + (Number(payment.ownerAmount) || 0),
    0
  );
  const currentBookingListingReview = listingReviews.find(
    (review) =>
      String(review.bookingId) === String(dynamicId) &&
      String(review.reviewerId) === String(auth.currentUser?.uid)
  );
  const renterSubmittedListingReview = listingReviews.find(
    (review) =>
      String(review.bookingId) === String(dynamicId) &&
      String(review.reviewerId) === String(bookingData?.renter)
  );
  const isTripReturned =
    bookingData?.status === "Trip Returned" ||
    bookingData?.status === "Trip Completed";
  const inspectionPassed = isInspectionAllGood(bookingData);
  const isTripCompleted =
    isTripReturned || bookingData?.status === "Trip Concluded";
  const canReviewListing =
    isTripCompleted &&
    (userData?.role === "Renter" || bookingData?.renter === auth.currentUser?.uid);
  const listingReviewReady =
    listingReviewFormData.review.trim() &&
    listingReviewCategories.every(
      (category) => Number(listingReviewFormData[category]) > 0
    );

  const getOwnerCutPercentage = () => {
    if (ownerCommissionRate != null) {
      return ownerCommissionRate;
    }
    const stored = Number(paymentDetails?.cutPercentage);
    if (Number.isFinite(stored) && stored >= 0) {
      return stored;
    }
    return 0.1;
  };

  const DEFAULT_COMMISSION_RATE = 0.1;

  const getCommissionRatePercent = () =>
    Math.round(getOwnerCutPercentage() * 100);

  const hasReducedCommissionRate = () =>
    getOwnerCutPercentage() < DEFAULT_COMMISSION_RATE;

  const getOwnerReferralCode = () => {
    const code = ownerData?.refferalCode?.trim();
    return code || null;
  };

  const calculateCommissionAmount = (offerPrice) => {
    if (!offerPrice) return "0.00";
    return (offerPrice * getOwnerCutPercentage()).toFixed(2);
  };

  const renderCommissionRateDetails = (offerPrice) => {
    if (!offerPrice) return null;

    const commissionPercent = getCommissionRatePercent();
    const commissionAmount = calculateCommissionAmount(offerPrice);
    const referralCode = getOwnerReferralCode();
    const isReduced = hasReducedCommissionRate();

    return (
      <>
        <hr />
        <div className="row">
          <div className="col-sm-4">
            <p className="mb-0">BBR Commission ({commissionPercent}%)</p>
          </div>
          <div className="col-sm-8">
            <p className="text-muted mb-0">-${commissionAmount}</p>
            <p className="mb-0" style={{ fontSize: "0.85em", color: "#6c757d" }}>
              {isReduced && referralCode
                ? `${commissionPercent}% commission rate applied via referral code "${referralCode}" (reduced from 10% default)`
                : isReduced
                  ? `${commissionPercent}% commission rate applied (reduced from 10% default)`
                  : `${commissionPercent}% standard commission rate`}
            </p>
          </div>
        </div>
      </>
    );
  };

  const getOwnerSharePercentage = () =>
    Math.round((1 - getOwnerCutPercentage()) * 100);

  const getFirstRentalFeeDollars = () => {
    const offerPrice = Number(inquiryData?.offerData?.offerPrice) || 0;
    if (!paymentDetails) return 0;

    if (paymentDetails.firstRentalPayment) {
      if (Number(paymentDetails.adminFirstTimeCut) > 0) {
        return paymentDetails.adminFirstTimeCut / 100;
      }
      if (paymentDetails.firstTimeSpecialRate) {
        return offerPrice * 0.5;
      }
      if (paymentDetails.referrer && paymentDetails.referAmount) {
        return 50;
      }
      return offerPrice >= 200 ? 100 : offerPrice > 0 ? offerPrice * 0.5 : 0;
    }

    // Advance bookings (>14 days) only charge a deposit up front, so the
    // first-rental flag is sometimes missing. The $100 still comes out of
    // the owner's first completed payout.
    const isAdvanceBooking =
      paymentDetails.advanceBooking === 1 ||
      paymentDetails.advanceBooking === true ||
      Number(paymentDetails.remainingAmount) > 0;
    if (!isAdvanceBooking || ownerPaidBookings !== 0) return 0;
    if (offerPrice >= 200) return 100;
    if (offerPrice > 0) return offerPrice * 0.5;
    return 0;
  };

  const calculateOwnerAmountFromOffer = (offerPrice) => {
    const base = offerPrice * (1 - getOwnerCutPercentage());
    return (base - getFirstRentalFeeDollars()).toFixed(2);
  };

  const renderFirstRentalFeeRow = (note) => {
    const fee = getFirstRentalFeeDollars();
    if (fee <= 0) return null;

    const isSpecialRate =
      Boolean(paymentDetails?.firstTimeSpecialRate) ||
      (!paymentDetails?.firstRentalPayment && fee < 100);

    return (
      <>
        <hr />
        <div className="row">
          <div className="col-sm-4">
            <p className="mb-0">First Rental Fee</p>
          </div>
          <div className="col-sm-8">
            <p className="text-muted mb-0">
              -${fee.toFixed(2)}{" "}
              <span style={{ fontSize: "0.85em" }}>
                {isSpecialRate
                  ? "(50% deducted — booking under $200, first rental)"
                  : note}
              </span>
            </p>
          </div>
        </div>
      </>
    );
  };

  const claimChargeLines = getDamageClaimChargeLines(bookingData);
  const chargedClaimTotal = claimChargeLines
    .filter((line) => line.status === "charged")
    .reduce((sum, line) => sum + Number(line.amount || 0), 0);
  const claimRequiresRenterApproval =
    requiresRenterApprovalForInspection(bookingData);
  const canDirectChargeFromHold =
    allowsDirectChargeFromInspection(bookingData);

  const renderClaimChargePaymentRows = () => {
    if (!claimChargeLines.length) {
      return null;
    }

    const statusLabel = {
      pending_renter_approval: "Pending renter approval",
      approved: "Approved",
      charged: "Charged",
    };

    return (
      <>
        <hr />
        {claimChargeLines.map((line, index) => (
          <div className="row" key={`${line.key}-${index}`}>
            <div className="col-sm-4">
              <p className="mb-0">{line.label}</p>
            </div>
            <div className="col-sm-8">
              <p className="text-muted mb-0">
                ${formatCurrency(line.amount)}
                {line.status && line.status !== "charged" ? (
                  <span style={{ fontSize: "0.85em" }}>
                    {" "}
                    ({statusLabel[line.status] || line.status})
                  </span>
                ) : null}
              </p>
            </div>
          </div>
        ))}
      </>
    );
  };

  const StarRatingInput = ({ rating, onRatingChange }) => {
    const stars = [1, 2, 3, 4, 5];

    return (
      <div>
        {stars.map((star) => (
          <span
            key={star}
            onClick={() => onRatingChange(star)}
            style={{
              cursor: "pointer",
              color: star <= rating ? "gold" : "gray",
              fontSize: "26px",
            }}
          >
            &#9733;
          </span>
        ))}
      </div>
    );
  };

  const renderReviewStars = (rating) => (
    <div className="d-flex align-items-center">
      {[1, 2, 3, 4, 5].map((star) => (
        <span
          key={star}
          style={{
            color: star <= Number(rating) ? "gold" : "gray",
            fontSize: "20px",
            lineHeight: 1,
          }}
        >
          &#9733;
        </span>
      ))}
    </div>
  );

  const getReviewRating = (review) => {
    const directRating = Number(review?.rating);

    if (directRating > 0) {
      return directRating;
    }

    const categoryRatings = listingReviewCategories
      .map((category) => Number(review?.[category]))
      .filter((rating) => Number.isFinite(rating) && rating > 0);

    if (!categoryRatings.length) {
      return 0;
    }

    return (
      categoryRatings.reduce((total, rating) => total + rating, 0) /
      categoryRatings.length
    );
  };

  const getReviewerName = (review) => {
    const name = `${review?.firstname || ""} ${review?.lastname || ""}`.trim();
    if (name) {
      return name;
    }
    if (renterData) {
      return `${renterData.firstName || ""} ${renterData.lastName || ""}`.trim();
    }
    return "Renter";
  };

  const renderSubmittedReviewDetails = (review) => {
    const categoryRatings = listingReviewCategories.filter(
      (category) => review?.[category] !== undefined && Number(review[category]) > 0
    );

    return (
      <>
        <h5 className="mb-3">{getReviewerName(review)}</h5>
        {categoryRatings.length > 0 ? (
          <Row className="mb-3">
            {categoryRatings.map((category) => (
              <Col xs={6} md={4} className="mb-3" key={category}>
                <p className="mb-1 fw-semibold">{category}</p>
                {renderReviewStars(review[category])}
              </Col>
            ))}
          </Row>
        ) : (
          <div className="mb-3">
            <p className="mb-1 fw-semibold">Rating</p>
            {renderReviewStars(getReviewRating(review))}
          </div>
        )}
        <div>
          <p className="mb-1 fw-semibold">Review</p>
          <p className="mb-0 text-muted">
            {review.review || "No written review provided."}
          </p>
        </div>
      </>
    );
  };

  const renderReviewSubmittedMessage = () => {
    if (!renterSubmittedListingReview) {
      return null;
    }

    return (
      <div
        className="d-flex align-items-center justify-content-between gap-3 px-3 py-3 rounded"
        style={{ backgroundColor: "#e5f8ff", color: "#0b4b6f" }}
      >
        <span>Renter has submitted a review for this booking.</span>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => setShowSubmittedReviewModal(true)}
        >
          View Review
        </Button>
      </div>
    );
  };

  const handleDownloadReceipt = async () => {
    if (!receiptRef.current) return;
    setIsGeneratingPDF(true);
    try {
      const canvas = await html2canvas(receiptRef.current, { scale: 2, windowWidth: 790, useCORS: true });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`BBR_BookingReceipt_${dynamicId}.pdf`);
    } catch (error) {
      console.error("Failed to generate receipt", error);
      toast.error("Failed to generate receipt");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleViewWaiver = async (passenger) => {
    setSelectedWaiver(passenger);
    setShowWaiverModal(true);
    setWaiverDescLoading(true);
    try {
      const waiverName = passenger.type === "Adult" ? "Adult Waiver" : "Minor Waiver";
      const desc = await getWaiverDescriptionByName(waiverName);
      setWaiverDescription(desc || "");
    } catch (e) {
      console.error("Error fetching waiver description:", e);
      setWaiverDescription("");
    }
    setWaiverDescLoading(false);
  };

  const closeDisputeModal = () => {
    setDisputeModalOpen(false);
    if (location.state?.showDamageClaimModal) {
      Navigate(`/dashboard/event/${dynamicId}`, { replace: true, state: {} });
    }
  };

  const openDisputeModal = () => {
    if (isInspectionAllGood(bookingData)) {
      return;
    }
    if (!canOwnerSubmitDamageClaim(bookingData)) {
      return;
    }
    setDisputeModalOpen(true);
  };

  const handleGoBackToInspection = async () => {
    try {
      const database = getDatabase();
      const tripDataRef = ref(database, `bookings/${dynamicId}/tripData`);
      await update(tripDataRef, { inspectionAllGood: false });
      Navigate(`/dashboard/endTrip/${dynamicId}`);
    } catch (goBackError) {
      console.error("Error returning to inspection:", goBackError);
      Navigate(`/dashboard/endTrip/${dynamicId}`);
    }
  };

  const [showSendWaiverEmailModal, setSendWaiverEmailModal] = useState(false);

  const [success, setSuccess] = useState("");

  const openSendWaiverEmailModal = () => {
    setSendWaiverEmailModal(true);
  };

  const closeSendWaiverEmailModal = () => {
    setSendWaiverEmailModal(false);
  };

  const [isRequestDisuputeModalOpen, setRequestDisputeModalOpen] =
    useState(false);

  const [error, setError] = useState("");

  const closeRequestDisputeModal = () => {
    setRequestDisputeModalOpen(false);
  };

  const openRequestDisputeModal = () => {
    setRequestDisputeModalOpen(true);
  };

  const [currentUserDetails, setCurrentUserDetails] = useState([]);

  const handleListingReviewInputChange = (e) => {
    const { name, value } = e.target;
    setListingReviewFormData({ ...listingReviewFormData, [name]: value });
  };

  const handleListingCategoryRatingChange = (category, rating) => {
    const updatedFormData = {
      ...listingReviewFormData,
      [category]: rating,
    };

    const categoryRatings = listingReviewCategories
      .map((reviewCategory) => Number(updatedFormData[reviewCategory]))
      .filter((value) => Number.isFinite(value) && value > 0);

    const averageRating = categoryRatings.length
      ? categoryRatings.reduce((total, value) => total + value, 0) /
        categoryRatings.length
      : 0;

    setListingReviewFormData({
      ...updatedFormData,
      rating: averageRating,
    });
  };

  const resetListingReviewForm = () => {
    setListingReviewFormData({
      review: "",
      rating: 0,
      "Listing Accuracy": 0,
      "Itinerary & Experience": 0,
      "Vessel & Equipment": 0,
      "Departure & Return": 0,
      Value: 0,
      Communication: 0,
    });
  };

  const handleListingReviewSubmit = async (event) => {
    event.preventDefault();

    if (!bookingData?.listingId || currentBookingListingReview || !listingReviewReady) {
      return;
    }

    try {
      const database = getDatabase();
      const reviewsRef = ref(database, `reviews/${bookingData.listingId}`);
      const newReviewRef = push(reviewsRef);
      const reviewData = {
        firstname: userData?.firstName || "",
        lastname: userData?.lastName || "",
        reviewerId: auth.currentUser?.uid || "",
        bookingId: dynamicId,
        listingId: bookingData.listingId,
        renterId: bookingData.renter || "",
        review: listingReviewFormData.review,
        rating: listingReviewFormData.rating,
        timestamp: serverTimestamp(),
      };

      listingReviewCategories.forEach((category) => {
        reviewData[category] = listingReviewFormData[category];
      });

      await set(newReviewRef, reviewData);
      const updatedReviews = await getReviewsByListingId(bookingData.listingId);
      setListingReviews(updatedReviews);
      resetListingReviewForm();
      setShowListingReviewModal(false);
      setSuccess("Review has been submitted.");
      setTimeout(() => setSuccess(""), 1500);

      const reviewerName = `${userData?.firstName || ""} ${userData?.lastName || ""}`.trim();
      notifyOwnerReviewReceived(
        bookingData.owner,
        bookingData.listingTitle,
        dynamicId,
        reviewerName,
        listingReviewFormData.rating
      ).catch((err) =>
        console.error("Failed to create owner review notification:", err)
      );
    } catch (error) {
      console.error("Error adding listing review data to Firebase:", error);
      setError("Failed to submit review.");
    }
  };

  // Edit Booking State
  const [editBookingModalOpen, setEditBookingModalOpen] = useState(false);

  const handleEditBookingOpen = () => {
    setEditBookingModalOpen(true);
  };






  const [startTripLoading, setStartTripLoading] = useState(false);

  const handleStartTrip = async () => {
    try {
      const isCaptain =
        inquiryData?.offerData?.captainCheckbox ||
        boatDetails?.boatType === "Fishing Guide" ||
        boatDetails?.boatType === "Fishing Charter" ||
        boatDetails?.boatCaptain === true;
      const isAdmin = userData?.role === "Admin";

      if (isCaptain || isAdmin) {
        setStartTripLoading(true);
        const database = getDatabase();

        if (isAdmin && !isCaptain && bookingData.paymentId) {
          await recordOffPlatformDeposit(
            bookingData.paymentId,
            boatDetails?.boatType,
            userData.userId
          );
        }

        const bookingRef = ref(database, `bookings/${dynamicId}`);
        await update(bookingRef, { status: "tripStarted" });

        const tripData = ref(database, `bookings/${dynamicId}/tripData`);
        await update(tripData, { startTimestamp: serverTimestamp() });

        const renterDetails = await getUserByID(bookingData.renter);
        const renterName = renterDetails.firstName + " " + renterDetails.lastName;

        const emailBody = await tripStatusUpdateEmailBody(
          renterName,
          bookingData.listingTitle,
          "started"
        );
        sendEmail(renterDetails.email, "Trip Started", emailBody);

        const renterSmsBody = `Your trip of ${bookingData.listingTitle} has now started.`;
        await sendSMS(renterDetails.phone, renterSmsBody);

        const ownerDetails = await getUserByID(bookingData.owner);
        const ownerName = ownerDetails?.firstName + " " + ownerDetails?.lastName;
        const emailOwnerBody = await tripStatusUpdateEmailBody(
          ownerName,
          bookingData.listingTitle,
          "started"
        );
        sendEmail(ownerDetails.email, "Trip Started", emailOwnerBody);

        const smsBody = `Trip of ${bookingData.listingTitle} with renter ${renterName} has now started.`;
        await sendSMS(ownerDetails.phone, smsBody);

        notifyOwnerTripStarted(
          bookingData.owner,
          bookingData.listingTitle,
          dynamicId,
          renterName
        ).catch((err) =>
          console.error("Failed to create owner notification:", err)
        );

        setStartTripLoading(false);
        closeStartTripModal();
        Navigate(`/dashboard/event/${dynamicId}`);
        window.location.reload();
      } else {
        Navigate("/dashboard/startTrip/" + dynamicId);
      }
    } catch (error) {
      setStartTripLoading(false);
      console.log("Error: ", error.message);
    }
  };


  async function getMessages() {
    const db = getDatabase();
    const chatId = inquiryData?.chatId;
    const chatRef = ref(db, `chats/${chatId}/messages`);
    const inquiryIdToUpdate = bookingData?.inquiryId;

    get(chatRef)
      .then((snapshot) => {
        if (snapshot.exists()) {
          const messages = snapshot.val();
          let updates = {};

          // Find all messages that match the inquiryId
          Object.keys(messages).forEach((key) => {
            if (messages[key].inquiryId === inquiryIdToUpdate) {
              updates[`chats/${chatId}/messages/${key}/offer`] = 2; // Update offer
              updates[`chats/${chatId}/messages/${key}/offerStatus`] =
                "canceled";
              updates[`chats/${chatId}/messages/${key}/timestamp`] = Date.now(); // Update timestamp
            }
          });

          if (Object.keys(updates).length > 0) {
            update(ref(db), updates)
              .then(() =>
                console.log("All matching messages updated successfully")
              )
              .catch((error) =>
                console.error("Error updating messages:", error)
              );
          } else {
            console.log("No messages found with the given inquiryId.");
          }
        } else {
          console.log("No messages found in this chat.");
        }
      })
      .catch((error) => {
        console.error("Error fetching messages:", error);
      });
  }

  const [cancelBookingLoading, setCancelBookingLoading] = useState(false);
  const [cancelBookingModal, setCancelBookingModal] = useState(false);

  const handleCancelBooking = async () => {
    console.log("inquiryDatainquiryData", inquiryData);

    setCancelBookingLoading(true);
    try {
      // Get payment details to check if we need to cancel a Stripe payment
      const database = getDatabase();

      // Get payment data
      const paymentRef = ref(database, `payments/${bookingData.paymentId}`);
      const paymentSnapshot = await get(paymentRef);
      const paymentData = paymentSnapshot.val();

      // If payment exists and is in authorized state, cancel it in Stripe
      if (
        paymentData &&
        paymentData.status === "authorized" &&
        paymentData.stripePaymentIntentId
      ) {
        try {
          console.log(dynamicId, "dynamic IDDD");
          // Call the backend endpoint to cancel the payment authorization in Stripe
          const response = await fetch(
            `${process.env.REACT_APP_BASE_URL}/cancel-booking`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                ...(auth.token ? { Authorization: `Bearer ${auth.token}` } : {}),
              },
              body: JSON.stringify({
                bookingId: dynamicId,
              }),
            }
          );

          const result = await response.json();

          if (!result.success) {
            console.error("Error canceling payment:", result.error);
            setError(`Failed to cancel payment: ${result.error}`);
            return;
          }
        } catch (error) {
          console.error("Error canceling payment:", error);
          setError("Failed to cancel payment. Please try again.");
          return;
        }
      } else {
        // For backward compatibility with existing records
        // Update pendingPayment if it exists
        const pendingPaymentRef = ref(
          database,
          `pendingPayments/${bookingData.paymentId}`
        );
        const pendingPaymentSnapshot = await get(pendingPaymentRef);

        if (pendingPaymentSnapshot.exists()) {
          await update(pendingPaymentRef, { status: "cancelled" });
        }

        // Update payment status if the payment record exists
        if (paymentData) {
          await update(paymentRef, {
            status: "cancelled",
            cancelledAt: new Date().toISOString(),
          });
        }
      }

      // Update booking status
      await updateBookingStatus(dynamicId, "cancelled");

      // Handle chat and inquiry updates - this stays mostly the same
      const [firstUserId, secondUserId] =
        bookingData?.owner < bookingData?.renter
          ? [bookingData?.owner, bookingData?.renter]
          : [bookingData?.renter, bookingData?.owner];

      const chatId1 = `${firstUserId}_${secondUserId}`;
      const chatId2 = `${secondUserId}_${firstUserId}`;

      // Update inquiry status
      const inquiryRef = ref(database, `inquiries/${bookingData?.inquiryId}`);
      await update(inquiryRef, { status: "canceled" });

      // Prepare cancellation message
      const totalGuests = getPassengerCounts(inquiryData?.inquiryData).total;
      const offerMessage = `This offer has been canceled:
    LineBreakLineBreak<span style="font-weight: bold;">Offer Details:</span>LineBreak
    • $${inquiryData?.inquiryData?.estimatedPrice} PriceLineBreak
    • Time offered, ${inquiryData?.inquiryData?.prefferedDate} at
     ${inquiryData?.inquiryData?.prefferedTime} LineBreak
    • Duration, ${Number(inquiryData?.inquiryData?.sameDayHours) > 0
          ? `${inquiryData?.inquiryData?.sameDayHours} ${Number(inquiryData?.inquiryData?.sameDayHours) > 1
            ? "Hours"
            : "Hour"
          } `
          : `${inquiryData?.inquiryData?.overnight} ${Number(inquiryData?.inquiryData?.overnight) > 1
            ? "Days"
            : "Night"
          }`
        }
        LineBreak
    ${Number(inquiryData?.inquiryData?.numberOfBoats) > 1 &&
        `• Total Boats rented, ${inquiryData?.inquiryData?.numberOfBoats} LineBreak`
        }
    ${totalGuests > 0 &&
        `• Total Guests, ${totalGuests} LineBreakLineBreak`
        }

    ${inquiryData?.inquiryData?.comments?.trim() !== "" &&
        `<span style="font-weight: bold;">Comment:</span>LineBreak ${inquiryData?.inquiryData?.comments} LineBreakLineBreak`
        }
`;

      const messageData = {
        senderId: bookingData?.owner,
        receiverId: bookingData?.renter,
        message: offerMessage,
        timestamp: serverTimestamp(),
        listingId: bookingData?.listingId,
        inquiryId: bookingData?.inquiryId,
        status: "canceled",
        offerStatus: null,
      };

      // Update chat messages
      const chatRef1 = ref(database, `chats/${chatId1}`);
      const chatRef2 = ref(database, `chats/${chatId2}`);

      const chatSnapshot1 = await get(chatRef1);
      const chatSnapshot2 = await get(chatRef2);
      let usedChatId;

      if (chatSnapshot1.exists()) {
        const updatedMessages = [...chatSnapshot1.val().messages]?.map(
          (message) => {
            if (message?.inquiryId === inquiryData?.id) {
              return { ...message, offerStatus: "Canceled" };
            }
            return message;
          }
        );

        usedChatId = chatId1;
        await update(chatRef1, {
          senderReadStatus: 1,
          receiverReadStatus: 0,
          lastSenderId: bookingData?.owner,
          messages: [...updatedMessages, messageData],
        });
      } else if (chatSnapshot2.exists()) {
        const updatedMessages = [...chatSnapshot2.val().messages]?.map(
          (message) => {
            if (message?.inquiryId === inquiryData?.id) {
              return { ...message, offerStatus: "Canceled" };
            }
            return message;
          }
        );

        usedChatId = chatId2;
        await update(chatRef2, {
          senderReadStatus: 1,
          receiverReadStatus: 0,
          lastSenderId: bookingData?.owner,
          messages: [...updatedMessages, messageData],
        });
      } else {
        usedChatId = chatId1;
        await set(chatRef1, {
          senderId: bookingData?.owner,
          receiverId: bookingData?.renter,
          senderReadStatus: 1,
          receiverReadStatus: 0,
          lastSenderId: bookingData?.owner,
          messages: [messageData],
        });
      }

      getMessages();
      // Hit the server's endpoint
      await fetch(`${process.env.REACT_APP_BASE_URL}/schedule-message`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          senderId: bookingData?.owner,
          receiverId: bookingData?.renter,
          lastSenderId: bookingData?.owner,
          chatId: usedChatId,
        }),
      });

      // Notify the renter about the cancellation via email only
      try {
        const renterData = await getUserByID(bookingData?.renter);

        if (renterData && renterData.email) {
          const renterName = `${renterData.firstName} ${renterData.lastName}`;

          // Send email notification
          const subjectEmail = "Booking Cancelled";
          const emailBody = `Hello ${renterName}, your booking for ${bookingData?.listingTitle} has been cancelled by the owner. Any payment authorization has been released back to your card, and no charges have been made.`;
          sendEmail(renterData.email, subjectEmail, emailBody);
        }
      } catch (error) {
        console.error("Error sending cancellation notification:", error);
      }

      setError("Your booking has been cancelled");
      setCancelBookingLoading(false);
      closeCancelBookingModal();
      Navigate(0);
    } catch (error) {
      console.log("Error: ", error.message);
      setError(`Failed to cancel booking: ${error.message}`);
    } finally {
      setCancelBookingLoading(false);
    }
  };

  const openCancelBookingModal = () => {
    setCancelBookingModal(true);
  };

  const closeCancelBookingModal = () => {
    setCancelBookingModal(false);
  };

  async function getLocations(lat, lng) {
    const location = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=AIzaSyBfEcoyW9DM7QQWdV3oTjevrfyhX5n5qqg`
    );

    const data = await location.json();
    console.log("data of locationsssssss-s-s-s-s-s-s", data);

    return data.results[0]?.formatted_address;
  }

  const copyToClipboard = (text) => {
    // Create a temporary input element
    const textArea = document.createElement("textarea");
    textArea.value = text;

    // Append the input element to the DOM
    document.body.appendChild(textArea);

    // Select the text in the input element
    textArea.select();
    document.execCommand("copy");

    // Remove the temporary input element from the DOM
    document.body.removeChild(textArea);
  };

  const handleCopyMinorWaiver = () => {
    const minorWaiverText = `${process.env.REACT_APP_BASE_URL}/waivers/minorWaiver/${dynamicId}`;
    copyToClipboard(minorWaiverText);
    alert("Link Copied");
  };

  const handleCopyAdultWaiver = () => {
    const adultWaiverText = `${process.env.REACT_APP_BASE_URL}/waivers/adultWaiver/${dynamicId}`;
    copyToClipboard(adultWaiverText);
    alert("Link Copied");
  };

  const [startTripHours, setStartTripHours] = useState(0);
  const [endTripHours, setEndTripHours] = useState(0);
  useEffect(() => {
    if (paymentDetails?.status === "Dispute Resolved") {
      setDisputeAmount(0);
    }
  }, [paymentDetails?.status]);
  useEffect(() => {
    let isMounted = true;
    let fetchGeneration = 0;

    const fetchUserData = async (bookingDetails, generation) => {
      const isCurrent = () => isMounted && generation === fetchGeneration;

      try {
        if (!bookingDetails) return; // No need to proceed if bookingDetails is null

        // Check if this is an instant booking (no inquiryId)
        // Package bookings create a real inquiry with offerData, so only treat as instant if no inquiryId
        const isInstantBooking = !bookingDetails.inquiryId;

        let inquiryDetails = null;

        if (isInstantBooking) {
          // For instant bookings, create inquiry-like data from booking details
          const duration = bookingDetails.duration || {};
          let durationText = duration.text || duration.duration || '';

          if (!durationText) {
            if (duration.overnight > 0) {
              durationText = `${duration.overnight} Night${duration.overnight > 1 ? 's' : ''}`;
            } else if (duration.sameDayHours > 0) {
              durationText = `${duration.sameDayHours} Hour${duration.sameDayHours > 1 ? 's' : ''}`;
              if (duration.sameDayMinutes > 0) {
                durationText += ` ${duration.sameDayMinutes} Minute${duration.sameDayMinutes > 1 ? 's' : ''}`;
              }
            }
          }

          inquiryDetails = {
            listingId: bookingDetails.listingId,
            inquiryData: {
              ...bookingDetails.passengers,
              prefferedDate: bookingDetails.tripDate,
              prefferedTime: bookingDetails.tripTime,
              sameDayHours: duration.sameDayHours || 0,
              overnight: duration.overnight || 0,
              estimatedPrice: bookingDetails.priceOffered,
              comments: bookingDetails.comments || '',
            },
            offerData: {
              offerPrice: bookingDetails.priceOffered,
              offeredDate: bookingDetails.tripDate,
              offeredTime: bookingDetails.tripTime,
              duration: durationText,
              offeredHours: duration.sameDayHours || 0,
              overnight: duration.overnight || 0,
              arivingDateTime: `${bookingDetails.tripDate} ${bookingDetails.tripTime}`,
            },
          };
          console.log("Created inquiry-like data for instant booking:", inquiryDetails);
        } else {
          console.log(
            "bookingDetails123",
            await getInquiryDataById(bookingDetails?.inquiryId)
          );
          inquiryDetails = await getInquiryDataById(bookingDetails.inquiryId);
        }
        if (!isCurrent()) return;
        console.log("inquiryDetails22222222222", inquiryDetails);

        // Check if we have string addresses saved from an offer update
        let pickupLocation;
        let dropOffLocation;

        // First check if we have string addresses from the updated offer
        if (inquiryDetails?.offerData?.pickupAddressString) {
          pickupLocation = inquiryDetails.offerData.pickupAddressString;
          console.log("Using saved pickup address string:", pickupLocation);
        }
        // Otherwise, get location from coordinates
        else if (
          inquiryDetails?.offerData?.pickupAddress?.lat &&
          inquiryDetails?.offerData?.pickupAddress?.lng
        ) {
          pickupLocation = await getLocations(
            inquiryDetails?.offerData?.pickupAddress?.lat,
            inquiryDetails?.offerData?.pickupAddress?.lng
          );
          if (!isCurrent()) return;
          console.log(
            "Resolved pickup location from coordinates:",
            pickupLocation
          );
        } else if (
          typeof inquiryDetails?.offerData?.pickupAddress === "string"
        ) {
          // Handle case where pickupAddress is directly a string
          pickupLocation = inquiryDetails.offerData.pickupAddress;
          console.log("Using direct pickup address string:", pickupLocation);
        } else {
          pickupLocation = "Location not available";
        }

        // First check if we have string addresses from the updated offer
        if (inquiryDetails?.offerData?.dropOffAddressString) {
          dropOffLocation = inquiryDetails.offerData.dropOffAddressString;
          console.log("Using saved dropoff address string:", dropOffLocation);
        }
        // Otherwise, get location from coordinates
        else if (
          inquiryDetails?.offerData?.dropOffAddress?.lat &&
          inquiryDetails?.offerData?.dropOffAddress?.lng
        ) {
          dropOffLocation = await getLocations(
            inquiryDetails?.offerData?.dropOffAddress?.lat,
            inquiryDetails?.offerData?.dropOffAddress?.lng
          );
          if (!isCurrent()) return;
          console.log(
            "Resolved dropoff location from coordinates:",
            dropOffLocation
          );
        } else if (
          typeof inquiryDetails?.offerData?.dropOffAddress === "string"
        ) {
          // Handle case where dropOffAddress is directly a string
          dropOffLocation = inquiryDetails.offerData.dropOffAddress;
          console.log("Using direct dropoff address string:", dropOffLocation);
        } else {
          dropOffLocation = "Location not available";
        }

        if (!isCurrent()) return;

        setDropOffLocation(dropOffLocation);
        setPickUpLocation(pickupLocation);

        // Use bookingDetails.listingId for instant bookings, otherwise use inquiryDetails.listingId
        const listingId = bookingDetails.listingId || inquiryDetails?.listingId;
        const listingDetails = await getListingByID(listingId);
        if (!isCurrent()) return;
        console.log("listingDetails...ttt", listingDetails);
        setBoatDetails(listingDetails || {});
        const timestamp = bookingDetails["timestamp"] || bookingDetails["createdAt"];
        const dateTime = new Date(timestamp);
        const currentUserDetails = await getCurrentUserDetails();
        if (!isCurrent()) return;
        setCurrentUserDetails(currentUserDetails);

        if (bookingDetails.tripData && bookingDetails.tripData.waivers) {
          if (
            bookingDetails.tripData.waivers[
            `Captain's Waiver & Equipment Checklist`
            ] &&
            bookingDetails.tripData.waivers[
            `Captain's Waiver & Equipment Checklist`
            ][0] &&
            bookingDetails.tripData.waivers[
            `Captain's Waiver & Equipment Checklist`
            ][0]["questions"] &&
            bookingDetails.tripData.waivers[
            `Captain's Waiver & Equipment Checklist`
            ][0]["questions"][12]
          ) {
            setStartTripHours(
              parseInt(
                bookingDetails.tripData.waivers[
                  `Captain's Waiver & Equipment Checklist`
                ][0]["questions"][12].answer
              )
            );
          }

          if (
            bookingDetails.tripData.waivers[`Boat Owner Equipment Checklist`] &&
            bookingDetails.tripData.waivers[
            `Boat Owner Equipment Checklist`
            ][0] &&
            bookingDetails.tripData.waivers[
            `Boat Owner Equipment Checklist`
            ][0]["questions"] &&
            bookingDetails.tripData.waivers[
            `Boat Owner Equipment Checklist`
            ][0]["questions"][5]
          ) {
            setEndTripHours(
              parseInt(
                bookingDetails.tripData.waivers[
                  `Boat Owner Equipment Checklist`
                ][0]["questions"][5].answer
              )
            );
          }
        }

        setPassengerNames(extractPassengerNamesFromBooking(bookingDetails));

        const formattedDateTime = dateTime.toLocaleDateString("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
          second: "numeric",
          hour12: false,
        });
        const ownerDetails = await getUserByID(bookingDetails["owner"]);
        const renterDetails = await getUserByID(bookingDetails["renter"]);
        if (!isCurrent()) return;
        let paidBookings = 0;
        try {
          const ownerMetaDetails = await getOwnerMeta(bookingDetails["owner"]);
          paidBookings = Number(ownerMetaDetails?.totalPaidBookings) || 0;
        } catch (ownerMetaError) {
          console.warn(
            "Could not load ownerMeta for first-rental display",
            ownerMetaError
          );
          paidBookings = null;
        }
        if (!isCurrent()) return;
        setOwnerPaidBookings(paidBookings);
        const ownerCommission = await getCommissionRateByReferralCode(
          ownerDetails?.refferalCode
        );
        if (!isCurrent()) return;

        const paymentData = await getPaymentById(bookingDetails.paymentId);
        if (!isCurrent()) return;
        console.log("paymentData......;", paymentData);
        const tipPaymentData = await getTipPaymentsByBookingId(dynamicId);
        const listingReviewData = await getReviewsByListingId(
          bookingDetails.listingId
        );

        const pendingPaymentData = await getPendingPaymentById(
          bookingDetails.paymentId
        );

        const disputeData = await getDisputeDetailsById(
          bookingDetails.disputeId
        );

        if (!isCurrent()) return;

        setDisputeDetails(disputeData);

        setPaymentDetails(paymentData);
        setTipPayments(tipPaymentData);
        setListingReviews(listingReviewData);
        setPendingPaymentDetails(pendingPaymentData);

        setListingData(listingDetails);
        setUserData(currentUserDetails);
        setInquiryData(inquiryDetails);
        const offer = inquiryDetails.offerData;

        const prefered = inquiryDetails?.inquiryData;
        const offerDate = dayjs(
          offer?.offeredDate ||
          prefered?.prefferedDate + "T" + offer?.offeredTime ||
          prefered?.prefferedTime
        );
        setOfferStartDateTime(offerDate);
        // bookingData is applied immediately in onValue to avoid stale status overwrites

        setTimeStamp(formattedDateTime);
        setOwnerData(ownerDetails);
        setOwnerCommissionRate(ownerCommission);
        setRenterData(renterDetails);
      } catch (error) {
        console.error("Error fetching user data:", error);
        if (!isCurrent()) return;
        setDropOffLocation("Location not found");
        setPickUpLocation("Location not found");
      }
    };

    const database = getDatabase();
    const bookingRef = ref(database, `bookings/${dynamicId}`);
    
    const unsubscribe = onValue(bookingRef, async (snapshot) => {
      if (snapshot.exists() && isMounted) {
        const bookingDetails = snapshot.val();
        const generation = ++fetchGeneration;
        // Apply booking snapshot immediately so status sync can't be undone by a
        // slower in-flight fetch that started with an older status.
        setBookingData(bookingDetails);
        setPassengerNames(extractPassengerNamesFromBooking(bookingDetails));
        await fetchUserData(bookingDetails, generation);
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [dynamicId]);

  const [showPostPaymentGuidanceModal, setShowPostPaymentGuidanceModal] =
    useState(false);
  const postPaymentGuidanceStorageKey = `postPaymentGuidanceSeen_${dynamicId}`;
  const [showOwnerPaymentReceivedGuidanceModal, setShowOwnerPaymentReceivedGuidanceModal] =
    useState(false);
  const ownerPaymentReceivedGuidanceStorageKey = `ownerPaymentReceivedGuidanceSeen_${dynamicId}`;
  const [showWaiversCompleteGuidanceModal, setShowWaiversCompleteGuidanceModal] =
    useState(false);
  const waiversCompleteGuidanceStorageKey = `waiversCompleteGuidanceSeen_${dynamicId}`;
  const [showPostTripRenterGuidanceModal, setShowPostTripRenterGuidanceModal] =
    useState(false);
  const postTripRenterGuidanceStorageKey = `postTripRenterGuidanceSeen_${dynamicId}`;
  const [showAllGoodConfirmationModal, setShowAllGoodConfirmationModal] =
    useState(false);
  const [showTripStartedGuidanceModal, setShowTripStartedGuidanceModal] =
    useState(false);
  const tripStartedGuidanceStorageKey = `tripStartedRenterGuidanceSeen_${dynamicId}`;

  const bookingForWaivers = bookingData
    ? { ...bookingData, inquiryData: inquiryData?.inquiryData }
    : bookingData;

  const waiverStatusSyncRef = React.useRef({
    bookingId: dynamicId,
    inFlight: false,
    lastWritten: null,
  });

  useEffect(() => {
    if (waiverStatusSyncRef.current.bookingId !== dynamicId) {
      waiverStatusSyncRef.current = {
        bookingId: dynamicId,
        inFlight: false,
        lastWritten: null,
      };
    }
  }, [dynamicId]);

  useEffect(() => {
    const syncWaiverFilledStatus = async () => {
      if (!bookingData || !boatDetails) {
        return;
      }

      const currentStatus = bookingData.status;
      const filledWaivers = Math.max(
        getFilledPassengerWaivers(bookingData),
        getSignedPassengerWaiverCount(bookingData)
      );
      const hasAnyWaiver = filledWaivers >= 1;

      // Only promote when a passenger waiver was actually signed.
      // required === 0 (solo renter-driven boat, or a bad party-size of 1)
      // must stay Waiver Pending until the captain acknowledges on the
      // Adult Waiver page — otherwise this page overwrites admin/heal
      // status changes back to Waiver Filled.
      const shouldMarkFilled =
        currentStatus === "Waiver Pending" && hasAnyWaiver;

      if (!shouldMarkFilled) {
        return;
      }

      if (
        waiverStatusSyncRef.current.inFlight ||
        waiverStatusSyncRef.current.lastWritten === "Waiver Filled"
      ) {
        return;
      }

      waiverStatusSyncRef.current.inFlight = true;
      try {
        await updateBookingStatus(dynamicId, "Waiver Filled");
        waiverStatusSyncRef.current.lastWritten = "Waiver Filled";
      } finally {
        waiverStatusSyncRef.current.inFlight = false;
      }
    };

    syncWaiverFilledStatus();
  }, [
    bookingData?.status,
    bookingData?.waiversFilled?.adult,
    bookingData?.waiversFilled?.minor,
    bookingData?.tripData?.waivers,
    boatDetails?.boatCaptain,
    boatDetails?.boatType,
    bookingData?.passengers?.total,
    bookingData?.totalWaivers,
    dynamicId,
  ]);

  // Trip Returned → Trip Concluded once hold is released or deposit charge is done
  useEffect(() => {
    const syncTripConcludedAfterHoldClosed = async () => {
      if (!bookingData || !paymentDetails) {
        return;
      }

      const status = (bookingData.status || "").toLowerCase();
      const isReturned =
        status === "trip returned" || status === "trip completed";
      if (!isReturned) {
        return;
      }

      const paymentStatus = (paymentDetails.status || "").toLowerCase();
      const holdStatus = (
        paymentDetails.paymentHeld?.status || ""
      ).toLowerCase();
      const holdClosed =
        paymentStatus === "refunded" ||
        paymentStatus === "dispute resolved" ||
        holdStatus === "released" ||
        holdStatus === "partially_captured" ||
        holdStatus === "canceled" ||
        holdStatus === "cancelled" ||
        holdStatus === "succeeded" ||
        bookingData.damageClaim?.status === "charged";

      if (!holdClosed) {
        return;
      }

      await updateBookingStatus(dynamicId, "Trip Concluded");
    };

    syncTripConcludedAfterHoldClosed();
  }, [
    bookingData?.status,
    bookingData?.damageClaim?.status,
    paymentDetails?.status,
    paymentDetails?.paymentHeld?.status,
    dynamicId,
  ]);

  useEffect(() => {
    if (!bookingData || !userData || !auth.currentUser) {
      return;
    }

    const isRenter =
      userData.role === "Renter" ||
      bookingData.renter === auth.currentUser.uid;
    const isOwner =
      userData.role === "Boat Owner" &&
      bookingData.owner === auth.currentUser.uid;

    if (
      isRenter &&
      bookingData.status === "Waiver Pending" &&
      !localStorage.getItem(postPaymentGuidanceStorageKey)
    ) {
      setShowPostPaymentGuidanceModal(true);
    }

    if (
      isOwner &&
      bookingData.status === "Waiver Pending" &&
      !localStorage.getItem(ownerPaymentReceivedGuidanceStorageKey)
    ) {
      setShowOwnerPaymentReceivedGuidanceModal(true);
    }

    if (
      isRenter &&
      bookingData.status === "Waiver Filled" &&
      arePassengerWaiversComplete(bookingForWaivers, boatDetails) &&
      getFilledPassengerWaivers(bookingData) >=
        getRequiredPassengerWaivers(bookingForWaivers, boatDetails) &&
      !localStorage.getItem(waiversCompleteGuidanceStorageKey)
    ) {
      setShowWaiversCompleteGuidanceModal(true);
    }

    if (
      isRenter &&
      bookingData.status === "tripStarted" &&
      isRenterDrivingTrip(boatDetails, inquiryData) &&
      (location.state?.showTripStartedGuidance ||
        !localStorage.getItem(tripStartedGuidanceStorageKey))
    ) {
      setShowTripStartedGuidanceModal(true);
    }

    if (
      isRenter &&
      isTripEndedStatus(bookingData.status) &&
      !localStorage.getItem(postTripRenterGuidanceStorageKey)
    ) {
      setShowPostTripRenterGuidanceModal(true);
    }

    if (location.state?.showAllGoodConfirmation) {
      setShowAllGoodConfirmationModal(true);
    }

    if (location.state?.showDamageClaimModal) {
      if (canOwnerSubmitDamageClaim(bookingData)) {
        setDisputeModalOpen(true);
      }
      Navigate(`/dashboard/event/${dynamicId}`, { replace: true, state: {} });
    }
  }, [
    bookingData,
    userData,
    auth.currentUser,
    boatDetails,
    postPaymentGuidanceStorageKey,
    ownerPaymentReceivedGuidanceStorageKey,
    waiversCompleteGuidanceStorageKey,
    postTripRenterGuidanceStorageKey,
    tripStartedGuidanceStorageKey,
    inquiryData,
    location.state?.showTripStartedGuidance,
    location.state?.showAllGoodConfirmation,
    location.state?.showDamageClaimModal,
  ]);

  useEffect(() => {
    if (
      isDisuputeModalOpen &&
      bookingData &&
      !canOwnerSubmitDamageClaim(bookingData)
    ) {
      setDisputeModalOpen(false);
    }
  }, [isDisuputeModalOpen, bookingData?.damageClaim?.status, bookingData]);

  const closePostPaymentGuidanceModal = () => {
    localStorage.setItem(postPaymentGuidanceStorageKey, "1");
    setShowPostPaymentGuidanceModal(false);
  };

  const closeOwnerPaymentReceivedGuidanceModal = () => {
    localStorage.setItem(ownerPaymentReceivedGuidanceStorageKey, "1");
    setShowOwnerPaymentReceivedGuidanceModal(false);
  };

  const closeWaiversCompleteGuidanceModal = () => {
    localStorage.setItem(waiversCompleteGuidanceStorageKey, "1");
    setShowWaiversCompleteGuidanceModal(false);
  };

  const closePostTripRenterGuidanceModal = () => {
    localStorage.setItem(postTripRenterGuidanceStorageKey, "1");
    setShowPostTripRenterGuidanceModal(false);
  };

  const handlePostTripReviewClick = () => {
    closePostTripRenterGuidanceModal();
    setError("");
    setShowListingReviewModal(true);
  };

  const handlePostTripDecline = () => {
    closePostTripRenterGuidanceModal();
  };

  const closeAllGoodConfirmationModal = () => {
    setShowAllGoodConfirmationModal(false);
    Navigate(`/dashboard/event/${dynamicId}`, { replace: true, state: {} });
  };

  const handleAllGoodGoBack = () => {
    setShowAllGoodConfirmationModal(false);
    handleGoBackToInspection();
  };

  const closeTripStartedGuidanceModal = () => {
    localStorage.setItem(tripStartedGuidanceStorageKey, "1");
    setShowTripStartedGuidanceModal(false);
    if (location.state?.showTripStartedGuidance) {
      Navigate(`/dashboard/event/${dynamicId}`, { replace: true, state: {} });
    }
  };

  const [startTripModal, setStartTripModal] = useState(false);
  const [startTripModalLoading, setStartTripModalLoading] = useState(false);
  const [showCaptainStartTripGuidanceModal, setShowCaptainStartTripGuidanceModal] =
    useState(false);
  const captainStartTripGuidanceStorageKey = `captainStartTripGuidanceSeen_${dynamicId}`;

  const shouldShowCaptainStartTripGuidanceForBooking = (bookingDetails) => {
    if (!bookingDetails || !userData || !auth.currentUser) {
      return false;
    }

    if (!isCaptainDrivenTrip(boatDetails, inquiryData)) {
      return false;
    }

    const isOwner =
      userData.role === "Boat Owner" &&
      bookingDetails.owner === auth.currentUser.uid;

    if (!isOwner && userData.role !== "Admin") {
      return false;
    }

    return !localStorage.getItem(captainStartTripGuidanceStorageKey);
  };

  const openStartTripModal = async () => {
    setStartTripModalLoading(true);
    let bookingDetails = null;
    try {
      bookingDetails = await getBookingOnce(dynamicId);
      if (bookingDetails) {
        setBookingData(bookingDetails);
        setPassengerNames(extractPassengerNamesFromBooking(bookingDetails));
      }
    } catch (error) {
      console.error("Error refreshing booking data for start trip:", error);
    } finally {
      setStartTripModalLoading(false);
      if (shouldShowCaptainStartTripGuidanceForBooking(bookingDetails)) {
        setShowCaptainStartTripGuidanceModal(true);
      } else {
        setStartTripModal(true);
      }
    }
  };

  const closeCaptainStartTripGuidanceModal = (openStartModal = false) => {
    localStorage.setItem(captainStartTripGuidanceStorageKey, "1");
    setShowCaptainStartTripGuidanceModal(false);
    if (openStartModal) {
      setStartTripModal(true);
    }
  };

  const closeStartTripModal = () => {
    setStartTripModal(false);
  };

  const handleDisputeAmount = (value) => {
    const securityAmount =
      boatDetails?.boatType?.toLowerCase() === "kayak"
        ? 200
        : boatDetails?.boatType?.toLowerCase() === "jetski"
          ? 500
          : 800;

    if (value <= securityAmount) {
      setDisputeAmount(parseFloat(value) || 0);
      setError("");
    } else if (!value) {
      setDisputeAmount(0);
    } else {
      setError("Amount should not exceed security deposit");
    }
  };

  const uploadDamageClaimReceipts = async (files) => {
    if (!files?.length) {
      return [];
    }

    const formData = new FormData();
    for (let index = 0; index < files.length; index += 1) {
      formData.append("files", files[index]);
    }

    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/upload-images`,
      {
        method: "POST",
        body: formData,
      }
    );

    if (!response.ok) {
      throw new Error("Failed to upload receipt images");
    }

    const data = await response.json();
    return data.file_names || [];
  };

  const submitDamageClaimForApproval = async () => {
    if (!disputeAmount || !disputeReason?.trim()) {
      setError("Enter the repair amount and a description for the renter.");
      return;
    }

    if (canDirectChargeFromHold && !claimRequiresRenterApproval) {
      setError(
        "Fuel and cleaning charges can be processed directly. Use the Charge button."
      );
      return;
    }

    if (bookingData?.damageClaim?.status === "pending_renter_approval") {
      setError("A damage claim is already waiting for renter approval.");
      return;
    }

    setDamageClaimSubmitting(true);
    setError("");

    try {
      const issueItems = getInspectionIssueItemsFromBooking(bookingData);
      const damageClaim = {
        status: "pending_renter_approval",
        amount: parseFloat(disputeAmount),
        reason: disputeReason.trim(),
        issueItems,
        categories: [
          ...new Set(
            issueItems.map((item) =>
              getInspectionIssueCategory(item.question, item.answer)
            )
          ),
        ],
        receiptUrls: damageClaimReceiptUrls,
        submittedAt: Date.now(),
        submittedBy: auth.currentUser.uid,
      };

      const database = getDatabase();
      const bookingRef = ref(database, `bookings/${dynamicId}`);
      await update(bookingRef, { damageClaim });

      const renterDetails = await getUserByID(bookingData.renter);
      const ownerDetails = await getUserByID(bookingData.owner);
      const bookingUrl = `${process.env.REACT_APP_BASE_URL}/dashboard/event/${dynamicId}`;
      const issueSummary = issueItems
        .map((item) => `• ${item.question}`)
        .join("LineBreak");
      const photoSummary = issueItems
        .flatMap((item) =>
          (item.photos || []).map(
            (photo) =>
              `• ${item.question} photo: ${formatUploadedAssetUrl(photo)}`
          )
        )
        .join("LineBreak");
      const receiptSummary = damageClaimReceiptUrls
        .map(
          (receipt, index) =>
            `• Repair receipt ${index + 1}: ${formatUploadedAssetUrl(receipt)}`
        )
        .join("LineBreak");
      const inboxMessage = `Damage claim submitted for ${bookingData.listingTitle}.LineBreakLineBreakRequested charge: $${parseFloat(disputeAmount).toFixed(2)}LineBreakReason: ${disputeReason.trim()}LineBreakLineBreakIssues found:LineBreak${issueSummary}${
        photoSummary
          ? `LineBreakLineBreakIssue photos:LineBreak${photoSummary}`
          : ""
      }${
        receiptSummary
          ? `LineBreakLineBreakRepair receipts:LineBreak${receiptSummary}`
          : ""
      }LineBreakLineBreakPlease review and approve or dispute this amount in your booking: ${bookingUrl}`;

      await sendBookingChatMessage({
        ownerId: bookingData.owner,
        renterId: bookingData.renter,
        senderId: bookingData.owner,
        receiverId: bookingData.renter,
        message: inboxMessage,
        listingId: bookingData.listingId,
        inquiryId: bookingData.inquiryId,
      });

      if (renterDetails?.phone) {
        await sendSMS(
          renterDetails.phone,
          `BBR: ${ownerDetails.firstName} requested a $${parseFloat(disputeAmount).toFixed(2)} security deposit charge for ${bookingData.listingTitle}. Review and approve in your booking: ${bookingUrl}`
        );
      }

      notifyRenterTripIssue(
        bookingData.renter,
        bookingData.listingTitle,
        dynamicId,
        bookingData.owner
      ).catch((err) =>
        console.error("Failed to create renter notification:", err)
      );

      if (renterDetails?.email) {
        const claimForPhrase = {
          ...bookingData,
          damageClaim: {
            issueItems,
            categories: [
              ...new Set(
                issueItems.map((item) =>
                  getInspectionIssueCategory(item.question, item.answer)
                )
              ),
            ],
          },
        };
        sendEmail(
          renterDetails.email,
          "Action required: review damage claim",
          `The boat owner reported ${formatRenterInspectionIssuePhrase(claimForPhrase)} on your trip and requested a charge of $${parseFloat(disputeAmount).toFixed(2)}. Review the details and approve or dispute here: ${bookingUrl}`
        );
      }

      setSuccess(
        "Damage claim sent to the renter. Their approval is required before any charge is processed."
      );
      setDamageClaimReceiptUrls([]);
      closeDisputeModal();
      Navigate(`/dashboard/event/${dynamicId}`, { replace: true, state: {} });
      window.location.reload();
    } catch (claimError) {
      console.error("Error submitting damage claim:", claimError);
      setError("Failed to submit damage claim. Please try again.");
    } finally {
      setDamageClaimSubmitting(false);
    }
  };

  const handleRenterApproveDamageClaim = async () => {
    if (bookingData?.damageClaim?.status !== "pending_renter_approval") {
      return;
    }

    setPaymentProcessing(true);
    setError("");

    try {
      const database = getDatabase();
      const bookingRef = ref(database, `bookings/${dynamicId}`);
      await update(bookingRef, {
        "damageClaim/status": "approved",
        "damageClaim/renterRespondedAt": Date.now(),
      });

      setDisputeAmount(bookingData.damageClaim.amount);
      // Captures only the approved claim amount; Stripe releases the unused hold remainder
      const charged = await captureCertainHeldPayment(
        bookingData.damageClaim.amount,
        true
      );

      if (!charged) {
        setError(
          "Damage claim was approved, but the security deposit charge failed. Please try again or contact support."
        );
        return;
      }

      const ownerDetails = await getUserByID(bookingData.owner);
      const renterDetails = await getUserByID(bookingData.renter);
      const bookingUrl = `${process.env.REACT_APP_BASE_URL}/dashboard/event/${dynamicId}`;
      const renterName = renterDetails
        ? `${renterDetails.firstName} ${renterDetails.lastName}`.trim()
        : "";

      if (ownerDetails?.phone) {
        await sendSMS(
          ownerDetails.phone,
          `BBR: The renter approved your $${parseFloat(bookingData.damageClaim.amount).toFixed(2)} damage claim for ${bookingData.listingTitle}.`
        );
      }

      await sendBookingChatMessage({
        ownerId: bookingData.owner,
        renterId: bookingData.renter,
        senderId: bookingData.renter,
        receiverId: bookingData.owner,
        message: `I approved the $${parseFloat(bookingData.damageClaim.amount).toFixed(2)} damage charge for ${bookingData.listingTitle}.LineBreakLineBreakView booking: ${bookingUrl}`,
        listingId: bookingData.listingId,
        inquiryId: bookingData.inquiryId,
      });

      notifyOwnerDamageClaimApproved(
        bookingData.owner,
        bookingData.listingTitle,
        dynamicId,
        bookingData.damageClaim.amount,
        renterName
      ).catch((err) =>
        console.error("Failed to create owner notification:", err)
      );

      setSuccess(
        `You approved the $${parseFloat(bookingData.damageClaim.amount).toFixed(2)} damage claim. That amount was charged from the security deposit hold; the remaining hold was released.`
      );
      closePostTripRenterGuidanceModal();
    } catch (approvalError) {
      console.error("Error approving damage claim:", approvalError);
      setError("Failed to approve damage claim. Please try again.");
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handleRenterRejectDamageClaim = async () => {
    if (bookingData?.damageClaim?.status !== "pending_renter_approval") {
      return;
    }

    try {
      const database = getDatabase();
      const bookingRef = ref(database, `bookings/${dynamicId}`);
      await update(bookingRef, {
        "damageClaim/status": "rejected",
        "damageClaim/renterRespondedAt": Date.now(),
      });

      const bookingUrl = `${process.env.REACT_APP_BASE_URL}/dashboard/event/${dynamicId}`;
      await sendBookingChatMessage({
        ownerId: bookingData.owner,
        renterId: bookingData.renter,
        senderId: bookingData.renter,
        receiverId: bookingData.owner,
        message: `I do not agree with the requested damage charge of $${parseFloat(bookingData.damageClaim.amount).toFixed(2)} for ${bookingData.listingTitle}. Please message me to resolve this: ${bookingUrl}`,
        listingId: bookingData.listingId,
        inquiryId: bookingData.inquiryId,
      });

      setSuccess("You disputed the damage claim. Please work with the boat owner through messaging.");
      closePostTripRenterGuidanceModal();
      window.location.reload();
    } catch (rejectError) {
      console.error("Error rejecting damage claim:", rejectError);
      setError("Failed to dispute damage claim. Please try again.");
    }
  };

  if (!bookingData || !inquiryData) {
    // You may want to display a loading spinner or message while data is being fetched
    return "";
  }

  const inquiry = inquiryData;

  const offer = inquiryData.offerData;
  let arrivingDate = new Date(offer?.offeredDate);

  arrivingDate?.setHours(
    arrivingDate?.getHours() +
    (parseInt(offer?.overnight) > 0
      ? parseInt(offer?.overnight) * 24
      : parseInt(offer?.offeredHours) > 0
        ? parseInt(offer?.offeredHours)
        : 0)
  );

  const hoursMatch = offer?.duration ? offer.duration.match(/(\d+) hours/) : null; // duration null
  const totalHours = hoursMatch ? parseInt(hoursMatch[1], 10) : 0;

  const tripDetails = bookingData.tripData;
  const mergedWaivers = {
    ...(tripDetails?.waivers?.["Adult Waiver"] || []),
    ...(tripDetails?.waivers?.["Minor Waiver"] || []),
    ...(tripDetails?.waivers?.["Boat Owner Equipment Checklist"] || []),
    ...(tripDetails?.waivers?.["Captain's Waiver & Equipment Checklist"] || []),
  };
  console.log('mergedWaivers...', mergedWaivers)
  const offerPriceAsNumber = Number(offer?.offerPrice);


  const sendWaiverEmail = async () => {
    try {
      const waiverTypeName = waiverType + " Waiver";
      if (waiverType === "Adult") {
        const emailBody = await sendWaiverBody(
          waiverTypeName,
          `${process.env.REACT_APP_BASE_URL}/waivers/adultWaiver/${dynamicId}`
        );
        await sendEmail(waiverReceiverEmail, "Waiver Received", emailBody);
        setSuccess("Email has been sent.");
      } else {
        const emailBody = await sendWaiverBody(
          waiverTypeName,
          `${process.env.REACT_APP_BASE_URL}/waivers/minorWaiver/${dynamicId}`
        );
        await sendEmail(waiverReceiverEmail, "Waiver Received", emailBody);
        setSuccess("Email has been sent.");
      }

      // Introduce a delay of 1.3 seconds before closing the modal
      setTimeout(() => {
        closeSendWaiverEmailModal();
        setSuccess("");
      }, 1300);
    } catch (error) {
      setSuccess("");
    }
  };

  const offeredFormattedDateTime = formatDateTime(
    `${offer?.offeredDate} ${offer?.offeredTime}`
  );
  const FormattedDateTime = formatDateTime(
    `${offer?.offeredDate} ${offer?.offeredTime}`
  );

  const hasTripStarted = [
    "tripstarted",
    "trip returned",
    "trip concluded",
    "trip completed",
    "completed",
  ].includes(bookingData?.status?.toLowerCase());

  const formatTimestamp = (timestamp) => {
    if (!timestamp || !timestamp) {
      return "....";
    }

    const date = new Date(timestamp);
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
    };
    return date.toLocaleDateString("en-US", options);
  };

  const calculateDuration = (startTimestamp, endTimestamp) => {
    if (!startTimestamp || !endTimestamp) {
      return "....";
    }

    const startDateTime = new Date(startTimestamp); // Use function parameter
    const endDateTime = new Date(endTimestamp); // Use function parameter
    const durationInMillis = endDateTime - startDateTime;

    // Convert duration to a human-readable format (e.g., hours, minutes, seconds)
    const hours = Math.floor(durationInMillis / 3600000);
    const minutes = Math.floor((durationInMillis % 3600000) / 60000);
    const seconds = Math.floor((durationInMillis % 60000) / 1000);

    return `${hours} hours ${minutes} minutes`;
  };

  var offerTimestamp = "";
  var isEarlyArrival = "";
  var isLateArrival = "";

  if (
    !tripDetails &&
    tripDetails !== undefined &&
    !tripDetails.startTimestamp &&
    !tripDetails.endTimestamp
  ) {
    offerTimestamp = new Date(offer.arivingDateTime).getTime();
    isEarlyArrival = offerTimestamp < tripDetails.endTimestamp;
    isLateArrival = offerTimestamp > tripDetails.endTimestamp;
  }

  // Check if offerPriceAsNumber is a valid number
  const formattedOfferPrice = isNaN(offerPriceAsNumber)
    ? "Invalid Number"
    : offerPriceAsNumber.toLocaleString();

  const WaiversCard = ({ waivers }) => {
    if (!waivers || waivers == null) {
      return null;
    }
    console.log("userDatazzzzzzzzzzzz", userData);
    console.log("bookingdatabbbbbbbbbb", bookingData);

    const getWaiverImageSrc = (value) => {
      if (!value) {
        return "";
      }

      const answer =
        typeof value === "object" && value?.url ? value.url : value;

      if (typeof answer !== "string") {
        return "";
      }

      if (answer.startsWith("http") || answer.startsWith("data:image")) {
        return answer;
      }

      return `${process.env.REACT_APP_BASE_URL}/images/${answer}`;
    };

    const isSignatureQuestion = (questionText) =>
      (questionText || "").toLowerCase().includes("signature");

    const isImageAnswer = (answer) => {
      if (typeof answer === "string") {
        return (
          answer.startsWith("http") ||
          answer.startsWith("data:image") ||
          /\.(png|jpe?g|gif|webp)$/i.test(answer)
        );
      }

      return (
        Array.isArray(answer) &&
        answer.length > 0 &&
        (typeof answer[0] === "string" || answer[0]?.url)
      );
    };

    const renderTable = (nodeName, waiverName, questions, signedAt) => (
      <div className="col-12 col-md-6" key={nodeName}>
        <div className="card" style={{ maxHeight: "400px" }}>
          <div className="card-header">
            <h2 className="card-title">{`${waiverName} - ${questions[0]?.answer
              } ${questions[1]?.answer || ""}`}</h2>
            {signedAt && (
              <small className="text-muted" style={{ fontSize: "12px" }}>
                ✅ Signed: {new Date(signedAt).toLocaleString("en-US", { month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "numeric", hour12: true })}
              </small>
            )}
          </div>
          <div className="card-body waivers-body overflow-auto p-3">
            <table className="table">
              <thead>
                <tr>
                  <th>Question</th>
                  <th>Answer</th>
                </tr>
              </thead>
              <tbody>
                {questions.map((question, index) => (
                  <tr key={index}>
                    <td>{question.question}</td>
                    <td>
                      {isSignatureQuestion(question.question) ? (
                        <img
                          src={getWaiverImageSrc(question.answer)}
                          alt="Signature"
                          style={{ maxWidth: "150px", maxHeight: "150px" }}
                        />
                      ) : Array.isArray(question.answer) ? (
                        question.answer.map((item, i) => (
                          <img
                            key={i}
                            src={getWaiverImageSrc(item)}
                            alt="Image"
                            style={{ maxWidth: "150px", maxHeight: "150px" }}
                          />
                        ))
                      ) : isImageAnswer(question.answer) ? (
                        <img
                          src={getWaiverImageSrc(question.answer)}
                          alt="Image"
                          style={{ maxWidth: "150px", maxHeight: "150px" }}
                        />
                      ) : (
                        question.answer
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );

    return (
      <>
        <div className="row">
          {Object.entries(waivers).map(([nodeName, node]) => {
            if (node && node.questions && node.questions.length > 0) {
              return renderTable(nodeName, node.waiverName, node.questions, node.signedAt || null);
            }
            return null;
          })}
        </div>
      </>
    );
  };

  const columns = [
    { Header: "Name", accessor: "name" },
    { Header: "Date Time", accessor: "dateTime" },
    { Header: "Amount", accessor: "amount" },
    { Header: "Booking ID", accessor: "bookingId" },
    // Add more columns as needed
  ];

  const data = [
    {
      name: "John Doe",
      dateTime: "2023-01-15 10:30 AM",
      amount: 100.0,
      bookingId: "B12345",
    },
    {
      name: "Jane Doe",
      dateTime: "2023-02-02 03:45 PM",
      amount: 75.5,
      bookingId: "B67890",
    },
    // Add more data rows as needed
  ];

  const captureHeldPayment = async () => {
    try {
      const captureResponse = await fetch(
        `${process.env.REACT_APP_BASE_URL}/capture-held-payment`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            paymentIntentId: paymentDetails.paymentHeld.id,
          }),
        }
      );

      const captureResult = await captureResponse.json();
      console.log("Capture result: ", captureResult);
      const bookingId = paymentDetails.bookingId || dynamicId;
      await updateHeldPaymentStatus(bookingData.paymentId, "Dispute Resolved");
      if (bookingData.paymentId) {
        const database = getDatabase();
        await update(ref(database, `payments/${bookingData.paymentId}`), {
          tripCompleted: true,
          tripCompletedAt: new Date().toISOString(),
        });
      }
      await updateBookingStatus(bookingId, "Trip Concluded");
      await updateDisputeStatus(bookingData.disputeId, "Closed");

      const renterData = await getCurrentUserDetails();
      const subjectEmail = "Booking Completed";
      const renterName = renterData.firstName + " " + renterData.lastName;
      const bookingTitle = listingData.listingTitle + " - " + renterName;
      const emailBody = await BookingStatusUpdateEmailBody(
        renterName,
        bookingTitle,
        "Completed"
      );
      sendEmail(renterData.email, subjectEmail, emailBody);

      const ownerData = await getUserByID(bookingData.owner);
      const subjectOwnerEmail = "Booking Completed";
      const ownerName = ownerData.firstName + " " + ownerData.lastName;
      const emailOwnerBody = await BookingStatusUpdateEmailBody(
        ownerName,
        bookingTitle,
        "Completed"
      );
      sendEmail(ownerData.email, subjectOwnerEmail, emailOwnerBody);
    } catch (error) {
      console.error("Error capturing held payment:", error);
    }
  };

  const sendDispute = async () => {
    try {
      // Ensure that disputeAmount and disputeReason are defined and not empty
      if (
        disputeAmount === undefined ||
        disputeReason === undefined ||
        disputeReason === ""
      ) {
        console.error("Dispute amount or reason is undefined or empty.");
        return;
      }

      if (paymentDetails.status === "Disputed") {
        setError("This booking has already been disputed.");
        return;
      }

      const database = getDatabase();
      const disputesRef = ref(database, "disputes");
      const disputeData = {
        bookingId: paymentDetails.bookingId,
        listingId: listingData.id,
        filedBy: auth.currentUser.uid,
        amount: disputeAmount,
        reason: disputeReason,
        status: "Open",
        timestamp: serverTimestamp(),
      };

      const newDisputeRef = push(disputesRef, disputeData);

      // Extract the disputeId from the reference
      const disputeId = newDisputeRef.key;

      const bookingsRef = ref(database, `bookings/${paymentDetails.bookingId}`);

      await update(bookingsRef, { disputeId: disputeId });

      await updateHeldPaymentStatus(bookingData.paymentId, "Disputed");
      await updateBookingStatus(paymentDetails.bookingId, "Disputed");
      closeRequestDisputeModal();
    } catch (error) {
      console.error("Error filing dispute:", error);
    }
  };

  const captureCertainHeldPayment = async (
    amountOverride,
    skipApprovalCheck = false
  ) => {
    let chargeSucceeded = false;
    try {
      if (isOffPlatformDeposit(paymentDetails)) {
        setError(
          "No card hold exists for this trip. The security deposit was collected off-platform."
        );
        return false;
      }

      const inspectionFoundIssues =
        bookingData?.tripData?.inspectionAllGood === false;
      const claimApproved = bookingData?.damageClaim?.status === "approved";
      const needsRenterApproval = requiresRenterApprovalForInspection(bookingData);

      if (
        !skipApprovalCheck &&
        needsRenterApproval &&
        !claimApproved
      ) {
        setError(
          "The renter must approve this charge before it can be processed."
        );
        return false;
      }

      if (
        !skipApprovalCheck &&
        !needsRenterApproval &&
        !allowsDirectChargeFromInspection(bookingData) &&
        !claimApproved
      ) {
        setError(
          "The renter must approve this charge before it can be processed."
        );
        return false;
      }

      const chargeAmount = amountOverride ?? disputeAmount;
      const amountInCents = Math.round(parseFloat(chargeAmount) * 100);
      const bookingId = paymentDetails?.bookingId || dynamicId;
      const heldAmountCents = paymentDetails?.paymentHeld?.amount || 0;

      if (!paymentDetails?.paymentHeld?.id) {
        setError("No security deposit hold found for this booking.");
        return false;
      }

      if (!amountInCents || amountInCents <= 0) {
        setError("Charge amount must be greater than zero.");
        return false;
      }

      setPaymentProcessing(true);
      setError("");

      // Stripe partial capture: charge only amountToCapture; unused auth is auto-released
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 45000);

      let captureResponse;
      try {
        captureResponse = await fetch(
          `${process.env.REACT_APP_BASE_URL}/capture-certain-held-payment`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            signal: controller.signal,
            body: JSON.stringify({
              paymentIntentId: paymentDetails.paymentHeld.id,
              amountToCapture: amountInCents,
              bookingId,
            }),
          }
        );
      } finally {
        clearTimeout(timeoutId);
      }

      const rawBody = await captureResponse.text();
      let captureResult = {};
      try {
        captureResult = rawBody ? JSON.parse(rawBody) : {};
      } catch (parseError) {
        const preview = (rawBody || "").replace(/\s+/g, " ").slice(0, 120);
        setError(
          `Failed to charge payment: server returned a non-JSON response (${captureResponse.status}). ${preview}`
        );
        return false;
      }
      console.log("Capture result: ", captureResult);

      // Only proceed if payment capture succeeded
      if (captureResult.success) {
        const holdStatus =
          heldAmountCents > amountInCents ? "partially_captured" : "succeeded";
        const creditKey = `${bookingData.paymentId || bookingId}:${amountInCents}`;
        const alreadyRecorded =
          holdChargeCreditRef.current === creditKey ||
          bookingData?.damageClaim?.status === "charged" ||
          Number(paymentDetails?.paymentHeld?.amountCaptured) === amountInCents;
        holdChargeCreditRef.current = creditKey;

        await updateHeldPaymentStatus(bookingData.paymentId, "Dispute Resolved");

        if (bookingData.paymentId) {
          const database = getDatabase();
          await update(ref(database, `payments/${bookingData.paymentId}`), {
            tripCompleted: true,
            tripCompletedAt: new Date().toISOString(),
            "paymentHeld/status": holdStatus,
            "paymentHeld/amountCaptured": amountInCents,
            "paymentHeld/capturedAt": new Date().toISOString(),
          });
        }

        await updateBookingStatus(bookingId, "Trip Concluded");

        if (bookingData.disputeId) {
          await updateDisputeStatus(bookingData.disputeId, "Closed");
        }

        const database = getDatabase();
        const issueItems = getInspectionIssueItemsFromBooking(bookingData);
        const directChargeClaim = {
          status: "charged",
          amount: parseFloat(chargeAmount) || 0,
          reason: disputeReason?.trim() || "",
          issueItems,
          categories: [
            ...new Set(
              issueItems.map((item) =>
                getInspectionIssueCategory(item.question, item.answer)
              )
            ),
          ],
          chargedAt: Date.now(),
          chargeType: canDirectChargeFromHold ? "direct" : "approved",
        };

        const bookingRefItem = ref(database, `bookings/${bookingId}`);
        await update(bookingRefItem, {
          damageCharge: parseFloat(chargeAmount) || 0,
          damageClaim: directChargeClaim,
        });

        // A retry after Stripe already captured must not credit the owner twice.
        if (!alreadyRecorded) {
          const ownerMetaRef = ref(database, `ownerMeta/${bookingData.owner}`);
          const ownerMetaSnapshot = await get(ownerMetaRef);

          if (ownerMetaSnapshot.exists()) {
            const ownerMeta = ownerMetaSnapshot.val();
            const currentAmount = ownerMeta.payments?.currentAmount || 0;
            const totalAmount = ownerMeta.payments?.totalAmount || 0;

            await update(ownerMetaRef, {
              "payments/currentAmount": currentAmount + amountInCents,
              "payments/totalAmount": totalAmount + amountInCents,
            });
          } else {
            await set(ownerMetaRef, {
              payments: {
                currentAmount: amountInCents,
                totalAmount: amountInCents,
              },
            });
          }

          const securityAmount =
            boatDetails?.boatType?.toLowerCase() === "kayak"
              ? "$200"
              : boatDetails?.boatType?.toLowerCase() === "jetski"
                ? "$500"
                : "$800";

          const ownerData = await getCurrentUserDetails();
          const ownerName = ownerData.firstName + " " + ownerData.lastName;
          const emailOwnerBody = await BookingCompletedWithDisputeOwnerEmailBody(
            ownerName,
            bookingData.listingTitle,
            chargeAmount,
            securityAmount
          );
          sendEmail(
            ownerData.email,
            "Congratulations! Booking Completed",
            emailOwnerBody
          );

          const renterData = await getUserByID(bookingData.renter);
          const emailBody = await BookingCompletedWithDisputeRenterEmailBody(
            ownerName,
            bookingData.listingTitle,
            chargeAmount,
            securityAmount
          );
          sendEmail(
            renterData.email,
            "Congratulations! Booking Completed",
            emailBody
          );
        }

        setSuccess(
          `$${parseFloat(chargeAmount).toFixed(2)} charged from security deposit. Remaining hold released.`
        );

        const chargeBtn = document.querySelector(".charge-btn");
        if (chargeBtn) {
          chargeBtn.style.display = "none!important";
        }
        setTimeout(() => {
          closeDisputeModal();
          window.location.reload();
        }, 1500);
        chargeSucceeded = true;
        return true;
      } else {
        setError(
          `Failed to charge payment: ${
            captureResult.error || captureResult.details || "Unknown error"
          }`
        );
        console.error("Payment capture failed:", captureResult);
        return false;
      }
    } catch (error) {
      console.error("Error capturing held payment:", error);
      const message =
        error?.name === "AbortError"
          ? "Charge request timed out. Please try again."
          : error.message || "Unknown error";
      setError(`Error capturing held payment: ${message}`);
      return false;
    } finally {
      if (!chargeSucceeded) {
        setPaymentProcessing(false);
      }
    }
  };

  const releaseHeldFunds = async () => {
    try {
      const bookingId = paymentDetails?.bookingId || dynamicId;

      const concludeTrip = async (successMessage) => {
        // Mark hold released and close booking → Trip Concluded (green on calendar)
        await updateHeldPaymentStatus(bookingData.paymentId, "refunded");
        if (bookingData.paymentId) {
          const database = getDatabase();
          await update(ref(database, `payments/${bookingData.paymentId}`), {
            tripCompleted: true,
            tripCompletedAt: new Date().toISOString(),
            "paymentHeld/status": "released",
            "paymentHeld/releasedAt": new Date().toISOString(),
          });
        }
        await updateBookingStatus(bookingId, "Trip Concluded");

        const renterData = await getUserByID(bookingData.renter);
        const subjectEmail = "Congratulations! Booking Completed";
        const renterName = renterData.firstName + " " + renterData.lastName;
        const bookingTitle = listingData.listingTitle + " - " + renterName;
        const emailBody = await BookingStatusUpdateEmailBody(
          renterName,
          bookingTitle,
          "Completed"
        );
        sendEmail(renterData.email, subjectEmail, emailBody);

        const ownerData = await getCurrentUserDetails();
        const subjectOwnerEmail = "Congratulations! Booking Completed";
        const ownerName = ownerData.firstName + " " + ownerData.lastName;
        const emailOwnerBody = await BookingStatusUpdateEmailBody(
          ownerName,
          bookingTitle,
          "Completed"
        );
        sendEmail(ownerData.email, subjectOwnerEmail, emailOwnerBody);

        setSuccess(successMessage);

        const chargeBtn = document.querySelector(".charge-btn");
        if (chargeBtn) {
          chargeBtn.style.display = "none!important";
        }
        setTimeout(() => {
          closeDisputeModal();
          window.location.reload();
        }, 1500);
        closeDisputeModal();
      };

      if (isOffPlatformDeposit(paymentDetails)) {
        await concludeTrip(
          "Trip completed. Security deposit was collected off-platform."
        );
        return;
      }

      const captureResponse = await fetch(
        `${process.env.REACT_APP_BASE_URL}/release-funds`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            paymentIntentId: paymentDetails.paymentHeld.id,
          }),
        }
      );
      console.log("paymentDetails.bookingId", bookingId);
      const rawBody = await captureResponse.text();
      let captureResult = {};
      try {
        captureResult = rawBody ? JSON.parse(rawBody) : {};
      } catch (parseError) {
        setError(
          "Failed to release security deposit hold. Please try again."
        );
        return;
      }
      if (!captureResult.success && !captureResponse.ok) {
        setError(
          captureResult.error ||
            "Failed to release security deposit hold. Please try again."
        );
        return;
      }
      await concludeTrip("Security Deposit hold has been released.");
      console.log("Capture result: ", captureResult);
    } catch (error) {
      console.error("Error capturing held payment:", error);
      setSuccess("");
      setError("Failed to release security deposit hold. Please try again.");
    }
  };

  const openHandleRemoveModal = () => {
    setRemoveModal(true);
  };

  const closeRemoveOfferModal = () => {
    setRemoveModal(false);
  };

  const handleRemoveOffer = async () => {
    setRemoveBookingLoading(true);

    try {
      const database = getDatabase();
      const bookingRef = ref(database, `bookings/${dynamicId}`);
      await update(bookingRef, { isDeleted: true });
      toast.success(`Booking has been removed.`, {
        autoClose: 1000,
      });
      Navigate("/dashboard");
    } catch (error) {
      console.error("Error removing offer:", error);
      toast.error(`Oops! try again later :) === ${error?.message}`);
    } finally {
      setRemoveBookingLoading(false);
    }
  };

  const securityAmount =
    boatDetails?.boatType?.toLowerCase() === "kayak"
      ? "$200"
      : boatDetails?.boatType?.toLowerCase() === "jetski"
        ? "$500"
        : "$800";

  return (
    <>
      <style>{customStyles}</style>
      <Modal
        centered
        show={showPostPaymentGuidanceModal}
        onHide={closePostPaymentGuidanceModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>Your booking is confirmed!</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            Next you&apos;ll need to forward passenger liability waivers to any
            other passengers. The trip cannot start until all passengers have
            completed their waivers.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={closePostPaymentGuidanceModal}>
            Got it
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        centered
        show={showOwnerPaymentReceivedGuidanceModal}
        onHide={closeOwnerPaymentReceivedGuidanceModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>Payment received!</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            The renter just paid — you&apos;re locked in for this trip! Your
            calendar shows this booking in purple until waivers are done. Send
            waiver links from here, prep your boat, and keep chatting in the
            inbox for any last-minute trip details.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={closeOwnerPaymentReceivedGuidanceModal}
          >
            Got it
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        centered
        show={showWaiversCompleteGuidanceModal}
        onHide={closeWaiversCompleteGuidanceModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {getWaiversCompleteGuidanceTitle(boatDetails, inquiryData)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            {getWaiversCompleteGuidanceMessage(boatDetails, inquiryData)}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={closeWaiversCompleteGuidanceModal}>
            Got it
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        centered
        size="lg"
        show={showListingReviewModal}
        onHide={() => setShowListingReviewModal(false)}
      >
        <Form onSubmit={handleListingReviewSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Review Your Trip</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted mb-2">
              Share a few details about your experience with {bookingData?.listingTitle || "this booking"}.
            </p>
            <p className="mb-4">
              Reviewing as:{" "}
              <strong>
                {userData?.firstName} {userData?.lastName}
              </strong>
            </p>
            <Form.Group>
              <Form.Label>Review</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="review"
                value={listingReviewFormData.review}
                onChange={handleListingReviewInputChange}
                placeholder="What stood out about the boat, owner, communication, or overall trip?"
              />
            </Form.Group>
            <Row className="mt-3">
              {listingReviewCategories.map((category) => (
                <Col xs={6} md={4} key={category}>
                  <Form.Group>
                    <Form.Label className="mb-0">{category}</Form.Label>
                    <StarRatingInput
                      rating={listingReviewFormData[category]}
                      onRatingChange={(rating) =>
                        handleListingCategoryRatingChange(category, rating)
                      }
                    />
                  </Form.Group>
                </Col>
              ))}
            </Row>
            {error && (
              <Alert severity="error" className="mt-3 mb-0">
                {error}
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowListingReviewModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!listingReviewReady}>
              Submit Review
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      <Modal
        centered
        size="lg"
        show={showSubmittedReviewModal}
        onHide={() => setShowSubmittedReviewModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Renter Review</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {renterSubmittedListingReview ? (
            renderSubmittedReviewDetails(renterSubmittedListingReview)
          ) : (
            <Alert severity="info" className="mb-0">
              No renter review has been submitted for this booking yet.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSubmittedReviewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showReceiptPreview} onHide={() => setShowReceiptPreview(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Preview Receipt</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
            <ReceiptTemplate  
              bookingData={bookingData} 
              listingData={boatDetails} 
              userData={receiptViewRole ? { ...userData, role: receiptViewRole } : userData} 
              inquiryData={inquiryData} 
              dynamicId={dynamicId} 
              startTripHours={startTripHours} 
              endTripHours={endTripHours} 
              ownerData={ownerData}
              renterData={renterData}
              paymentDetails={paymentDetails}
              pickUpLocation={pickUpLocation}
              disputeDetails={disputeDetails}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReceiptPreview(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleDownloadReceipt} disabled={isGeneratingPDF}>
            {isGeneratingPDF ? "Downloading..." : "Download PDF"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Hidden fixed-width element strictly for perfect PDF extraction */}
      <div style={{ position: "absolute", top: "-9999px", left: "-9999px", width: "790px", backgroundColor: "#fff", zIndex: -1 }}>
        <ReceiptTemplate 
          ref={receiptRef} 
          bookingData={bookingData} 
          listingData={boatDetails} 
          userData={receiptViewRole ? { ...userData, role: receiptViewRole } : userData} 
          inquiryData={inquiryData} 
          dynamicId={dynamicId} 
          startTripHours={startTripHours} 
          endTripHours={endTripHours} 
          ownerData={ownerData}
          renterData={renterData}
          paymentDetails={paymentDetails}
          pickUpLocation={pickUpLocation}
          disputeDetails={disputeDetails}
          isPdf={true}
        />
      </div>

      <div className="row">
        <div className="col">
          {bookingData.status === "tripStarted" &&
            (userData?.role === "Renter" ||
              bookingData.renter === auth.currentUser?.uid) && (
              <Alert severity="success" className="mb-3">
                Your trip is in progress. Have fun and be safe!
              </Alert>
            )}
          <div className="page-description d-flex justify-content-between flex-wrap gap-3" style={{ position: "relative", zIndex: 99 }}>
            <h1>{bookingData.listingTitle}</h1>
            <div className="d-flex align-items-start flex-wrap" style={{ gap: "5px", position: "relative", zIndex: 99 }}>
              {currentUserDetails.role === "Admin" && (
                <Button
                  className="btn btn-warning d-flex align-items-center justify-content-center gap-1"
                  style={{ height: "48px" }}
                  onClick={openHandleRemoveModal}
                >
                  <RiDeleteBin6Line size={15} />
                  <span> Remove Booking</span>
                </Button>
              )}
              {/* Preview Receipt Buttons - Only for Renter/Admin when trip is completed */}
              {(currentUserDetails?.role === "Admin" || currentUserDetails?.role === "Renter" || auth?.currentUser?.uid === bookingData?.renter) && 
               ["trip concluded"].includes(bookingData?.status?.toLowerCase()) && (
                <Button
                  className="btn btn-secondary d-flex align-items-center justify-content-center gap-1 bg-light text-dark"
                  style={{ height: "48px", border: "1px solid #ccc" }}
                  onClick={() => { setReceiptViewRole("Renter"); setShowReceiptPreview(true); }}
                  disabled={isGeneratingPDF || !bookingData || !boatDetails}
                >
                  <RiDownloadLine size={15} />
                  <span>{currentUserDetails?.role === "Admin" ? "Renter Receipt" : "Download Receipt"}</span>
                </Button>
              )}
              {/* Global Message Button */}
              <Link
                to={`/dashboard/inbox?userId=${bookingData.renter === auth.currentUser.uid ? bookingData.owner : bookingData.renter}`}
                className="btn btn-message-custom d-flex align-items-center justify-content-center gap-1"
                style={{ height: "48px" }}
              >
                <RiMessage2Line size={15} />
                <span> Message</span>
              </Link>

              {bookingData.status === "Waiver Filled" &&
                (["boat owner"].includes(userData?.role?.toLowerCase()) || userData?.role === "Admin") &&
                ((inquiryData?.offerData?.captainCheckbox || boatDetails?.boatType === "Fishing Guide" || boatDetails?.boatType === "Fishing Charter" || boatDetails?.boatCaptain === true) || userData?.role === "Admin") &&
                (bookingData?.renter !== userData?.userId || userData?.role === "Admin") && (
                  <Button
                    onClick={openStartTripModal}
                    className="btn btn-warning d-flex align-items-center justify-content-center"
                    style={{ height: "48px" }}
                    disabled={startTripModalLoading}
                  >
                    {startTripModalLoading ? (
                      <ThreeDots
                        visible={true}
                        height="22"
                        width="22"
                        color="#fff"
                        radius="20"
                        ariaLabel="three-dots-loading"
                      />
                    ) : (
                      "Start Trip"
                    )}
                  </Button>
                )}
              {!["cancelled", "trip concluded", "trip completed", "trip returned"].includes(
                bookingData.status.toLowerCase()
              ) &&
                !["cancelled"].includes(bookingData.status.toLowerCase()) &&
                ["admin", "boat owner"].includes(userData.role.toLowerCase()) &&
                bookingData.renter !== auth.currentUser.uid &&
                !["tripstarted", "trip returned"].includes(bookingData.status.toLowerCase()) && (
                  <>
                    <Button
                      className="btn btn-danger"
                      onClick={openCancelBookingModal}
                      style={{ height: "48px" }}
                    >
                      Cancel Booking
                    </Button>
                    {bookingData.status.toLowerCase() === "pending" && !offer ? (
                      <Link
                        to={`/dashboard/inbox?userId=${bookingData.renter === auth.currentUser.uid ? bookingData.owner : bookingData.renter}`}
                        className="btn btn-secondary d-flex align-items-center gap-1"
                        style={{ height: "48px" }}
                      >
                        <RiEditLine size={15} /> Create Offer
                      </Link>
                    ) : (
                      <Button
                        className="btn btn-secondary d-flex align-items-center gap-1"
                        onClick={handleEditBookingOpen}
                        style={{ height: "48px" }}
                      >
                        <RiEditLine size={15} /> Edit Booking
                      </Button>
                    )}
                  </>
                )}
              {bookingData.status.toLowerCase() == "pending" &&
                userData.role === "Boat Owner" &&
                bookingData.renter != auth.currentUser.uid ? (
                <button className="btn btn-warning d-flex align-items-center justify-content-center" style={{ height: "48px" }}>Pending</button>
              ) : bookingData.status === "Cancelled" ? (
                <button className="btn btn-danger d-flex align-items-center justify-content-center" style={{ height: "48px" }}>Cancelled</button>
              ) : bookingData.status === "Completed" ? (
                <button className="btn btn-secondary d-flex align-items-center justify-content-center" style={{ height: "48px" }}>Trip Returned</button>
              ) : (bookingData.status === "Waiver Pending" ||
                bookingData.status === "Waiver Filled" ||
                bookingData.status === "tripStarted" ||
                bookingData.status === "Paid") &&
                (userData.role === "Renter" ||
                  bookingData.renter === auth.currentUser.uid) ? (
                <>
                  <div className="d-flex flex-column gap-2">
                    {bookingData.status === "Waiver Pending" && (
                      <>
                        <Alert severity="warning">
                          {getRequiredPassengerWaivers(bookingForWaivers, boatDetails) === 0
                            ? "As the boat captain, confirm your role on the Adult Passenger Waiver page to proceed. Your captain's waiver will be completed when you start the trip."
                            : "Submit passenger waivers before starting trip."}
                        </Alert>
                      </>
                    )}

                    <div className="d-flex gap-2 justify-content-center align-items-center flex-wrap">
                      <Link
                        to={"/waivers/adultWaiver/" + dynamicId}
                        state={{ returnToBooking: true }}
                        className="btn btn-primary d-flex align-items-center justify-content-center"
                        style={{ height: "48px" }}
                      >
                        Adult Passenger Waiver
                      </Link>

                      <Link
                        to={"/waivers/minorWaiver/" + dynamicId}
                        state={{ returnToBooking: true }}
                        className="btn btn-primary d-flex align-items-center justify-content-center"
                        style={{ height: "48px" }}
                      >
                        Minor Passenger Waiver
                      </Link>
                    </div>

                    <Button
                      className="send-waiver-button d-flex align-items-center justify-content-center"
                      onClick={openSendWaiverEmailModal}
                      style={{ height: "48px" }}
                    >
                      <RiMailSendLine /> Send Waivers
                    </Button>


                    {console.log("bookingData312", {
                      bookingData,
                      inquiryData,
                    })}

                    {bookingData.status === "Waiver Filled" &&
                      (!Boolean(inquiryData?.offerData?.captainCheckbox) || userData?.role === "Admin") &&
                      (bookingData?.renter === userData?.userId || userData?.role === "Admin") &&
                      (boatDetails?.boatType !== "Fishing Guide" || userData?.role === "Admin") &&
                      (boatDetails?.boatType !== "Fishing Charter" || userData?.role === "Admin") &&
                      (boatDetails?.boatCaptain !== true || userData?.role === "Admin") && (
                        <>
                          <Button
                            onClick={openStartTripModal}
                            className="btn btn-warning d-flex align-items-center justify-content-center"
                            style={{ height: "48px" }}
                            disabled={startTripModalLoading}
                          >
                            {startTripModalLoading ? (
                              <ThreeDots
                                visible={true}
                                height="22"
                                width="22"
                                color="#fff"
                                radius="20"
                                ariaLabel="three-dots-loading"
                              />
                            ) : (
                              "Start Trip"
                            )}
                          </Button>
                        </>
                      )}

                    {bookingData.status === "tripStarted" && userData?.role === "Admin" && (
                      <>
                        <Link
                          to={"/dashboard/endTrip/" + dynamicId}
                          className="btn btn-danger mt-2 d-flex align-items-center justify-content-center"
                          style={{ height: "48px" }}
                        >
                          End Trip
                        </Link>
                      </>
                    )}
                    {/* {(bookingData.status === "Waiver Pending" ||
                      bookingData.status === "Paid") &&
                      paymentDetails.advanceBooking === 1 &&
                      paymentDetails.remainingAmount !== 0 &&
                      pendingPaymentDetails.status === "pending" &&
                      new Date() - new Date(offer.offeredDate) <
                        1000 * 60 * 60 * 24 * 14 && (
                        <>
                          <Button
                            onClick={openCancelBookingModal}
                            className="btn btn-danger"
                          >
                            Cancel Booking
                          </Button>
                        </>
                      )} */}

                    {/* <Alert severity={(bookingData.waiversFilled.adult + bookingData.waiversFilled.minor) === bookingData.totalWaivers ? 'success' : 'warning'}>Waivers Filled {(bookingData.waiversFilled.adult + bookingData.waiversFilled.minor)}/{bookingData.totalWaivers}</Alert> */}
                  </div>
                </>
              ) : bookingData.status === "Waiver Pending" &&
                userData.role === "Boat Owner" &&
                bookingData.renter != auth.currentUser.uid ? (
                <>
                  <div className="d-flex gap-3">
                    <div className="d-flex align-items-center" style={{ height: "48px" }}>
                      <Alert severity="warning" className="mb-0 py-0 d-flex align-items-center" style={{ height: "100%" }}>
                        Waiting for waivers submission.
                      </Alert>
                    </div>
                    <div className="d-flex gap-3 justify-content-center align-items-center">
                      <Button
                        className="py-2"
                        onClick={openSendWaiverEmailModal}
                        style={{ height: "48px" }}
                      >
                        {" "}
                        <RiMailSendLine /> Send Waivers
                      </Button>
                    </div>
                  </div>
                </>
              ) : bookingData.status.toLowerCase() == "pending" &&
                (userData.role === "Renter" ||
                  (userData.role === "Boat Owner" &&
                    bookingData.renter === auth.currentUser.uid)) ? (
                !offer ? (
                  <div className="d-flex align-items-center" style={{ height: "48px" }}>
                    <Alert severity="info" className="mb-0 py-0 d-flex align-items-center" style={{ height: "100%" }}>
                      Waiting for boat owner to create offer.
                    </Alert>
                  </div>
                ) : !hasRenterAcceptedOffer(bookingData, inquiryData) ? (
                  <div className="d-flex align-items-center" style={{ height: "48px" }}>
                    <Alert severity="info" className="mb-0 py-0 d-flex align-items-center" style={{ height: "100%" }}>
                      Please accept the offer in the{" "}
                      <Link to={`/dashboard/inbox?userId=${bookingData.owner}`}>
                        inbox
                      </Link>{" "}
                      to proceed with payment.
                    </Alert>
                  </div>
                ) : (
                  <Link
                    to={`/dashboard/booking-payment/?bookingId=` + dynamicId}
                    className="btn btn-primary d-flex align-items-center justify-content-center"
                    style={{ height: "48px" }}
                  >
                    Pay Now
                  </Link>
                )
              ) : bookingData.status === "Paid" &&
                userData.role === "Boat Owner" &&
                bookingData.renter != auth.currentUser.uid ? (
                (boatDetails?.boatCaptain === true || boatDetails?.boatType === "Fishing Charter" || boatDetails?.boatType === "Fishing Guide" || inquiryData?.offerData?.captainCheckbox) ? (
                  <div className="d-flex align-items-center" style={{ height: "48px" }}>
                    <Alert severity="info" className="mb-0 py-0 d-flex align-items-center" style={{ height: "100%" }}>
                      Waiting for waivers to be filled to start trip.
                    </Alert>
                  </div>
                ) : (
                  <div className="d-flex align-items-center" style={{ height: "48px" }}>
                    <Alert severity="warning" className="mb-0 py-0 d-flex align-items-center" style={{ height: "100%" }}>
                      Waiting for renter to start trip.
                    </Alert>
                  </div>
                )
              ) : bookingData.status === "tripStarted" &&
                (userData.role === "Boat Owner" || userData.role === "Admin") &&
                (bookingData.renter != auth.currentUser.uid || userData.role === "Admin") ? (
                <>
                  <div className="d-flex flex-column gap-2 justify-content-center align-items-center flex-wrap">
                    <Link
                      to={"/waivers/adultWaiver/" + dynamicId}
                      state={{ returnToBooking: true }}
                      className="btn btn-primary d-flex align-items-center justify-content-center"
                      style={{ height: "48px" }}
                    >
                      Adult Passenger Waiver
                    </Link>

                    <Link
                      to={"/waivers/minorWaiver/" + dynamicId}
                      state={{ returnToBooking: true }}
                      className="btn btn-primary d-flex align-items-center justify-content-center"
                      style={{ height: "48px" }}
                    >
                      Minor Passenger Waiver
                    </Link>
                    <Button
                      className="send-waiver-button"
                      onClick={openSendWaiverEmailModal}
                    >
                      <RiMailSendLine /> Send Waivers
                    </Button>
                    <Link
                      to={"/dashboard/endTrip/" + dynamicId}
                      className="btn btn-danger d-flex align-items-center justify-content-center"
                      style={{ height: "48px" }}
                    >
                      End Trip
                    </Link>
                  </div>
                </>
              ) : isTripReturned &&
                userData.role === "Boat Owner" &&
                bookingData.renter != auth.currentUser.uid &&
                !(inquiryData?.offerData?.captainCheckbox || boatDetails?.boatType === "Fishing Guide" || boatDetails?.boatType === "Fishing Charter" || boatDetails?.boatCaptain === true) ? (
                <>
                  <div>
                    <div className="d-flex flex-column" style={{ gap: "5px" }}>
                      {totalTipAmount > 0 && (
                        <Alert severity="info" className="mb-0">
                          Renter gave ${formatCurrency(totalTipAmount / 100)} tip (excluded 3% Stripe charges).
                        </Alert>
                      )}
                      {renderReviewSubmittedMessage()}
                      {isOffPlatformDeposit(paymentDetails) && (
                        <Alert severity="info" className="mb-0">
                          Security deposit was collected off-platform (cash/Venmo).
                          No card hold to release or charge.
                        </Alert>
                      )}
                      {bookingData.damageClaim?.status ===
                        "pending_renter_approval" && (
                        <Alert severity="warning" className="mb-0">
                          Waiting for the renter to approve your $
                          {formatCurrency(bookingData.damageClaim.amount)}{" "}
                          damage claim before any charge can be processed.
                        </Alert>
                      )}
                      {bookingData.damageClaim?.status === "approved" && (
                        <Alert severity="info" className="mb-0">
                          The renter approved your $
                          {formatCurrency(bookingData.damageClaim.amount)}{" "}
                          damage claim. Process the charge to capture that
                          amount and release the remaining security deposit hold.
                        </Alert>
                      )}
                      {bookingData.damageClaim?.status === "charged" && (
                        <Alert severity="success" className="mb-0">
                          Damage charge of $
                          {formatCurrency(
                            bookingData.damageCharge ||
                              bookingData.damageClaim.amount
                          )}{" "}
                          was processed from the security deposit.
                        </Alert>
                      )}
                      {!inspectionPassed &&
                        bookingData.damageClaim?.status !==
                          "pending_renter_approval" &&
                        bookingData.damageClaim?.status !== "charged" &&
                        bookingData.damageClaim?.status !== "approved" && (
                          <Alert severity="warning" className="mb-0">
                            {canDirectChargeFromHold && !claimRequiresRenterApproval
                              ? "Inspection found a fuel surcharge or cleaning charge. Enter the amount and charge it directly from the renter's security deposit hold."
                              : "Inspection found damage or other issues. Submit a claim so the renter can review photos and receipts before any charge is processed."}
                          </Alert>
                        )}
                      {inspectionPassed && !isOffPlatformDeposit(paymentDetails) && (
                        <Alert severity="info" className="mb-0">
                          If All Good, click Cancel Hold to release the renter&apos;s
                          security deposit. Go Back if there is a fuel surcharge,
                          cleaning charge, or other issue.
                        </Alert>
                      )}
                      <div className="charge-btn d-flex" style={{ gap: "5px" }}>
                        {paymentDetails?.status !== "Dispute Resolved" &&
                          !isOffPlatformDeposit(paymentDetails) &&
                          (inspectionPassed ? (
                            <Button
                              onClick={handleGoBackToInspection}
                              className="btn btn-primary"
                            >
                              Go Back
                            </Button>
                          ) : (
                            canOwnerSubmitDamageClaim(bookingData) && (
                              <Button
                                onClick={openDisputeModal}
                                className="btn btn-primary"
                              >
                                {canDirectChargeFromHold && !claimRequiresRenterApproval
                                  ? "Charge from Hold"
                                  : "Submit Damage Claim"}
                              </Button>
                            )
                          ))}
                        {bookingData.damageClaim?.status === "approved" &&
                          paymentDetails?.status !== "Dispute Resolved" &&
                          !isOffPlatformDeposit(paymentDetails) && (
                            <Button
                              onClick={() =>
                                captureCertainHeldPayment(
                                  bookingData.damageClaim.amount
                                )
                              }
                              className="btn btn-warning"
                              disabled={paymentProcessing}
                            >
                              {paymentProcessing
                                ? "Processing..."
                                : `Charge $${formatCurrency(
                                    bookingData.damageClaim.amount
                                  )}`}
                            </Button>
                          )}
                        {!(
                          bookingData.damageClaim?.status ===
                            "pending_renter_approval" ||
                          bookingData.damageClaim?.status === "approved" ||
                          bookingData.damageClaim?.status === "charged" ||
                          paymentDetails?.status === "Dispute Resolved"
                        ) && (
                        <Button
                          onClick={releaseHeldFunds}
                          className="btn btn-success"
                        >
                          {isOffPlatformDeposit(paymentDetails)
                            ? "Complete Trip"
                            : "Cancel Hold"}
                        </Button>
                        )}
                      </div>
                      <Alert severity="info">
                        {endTripHours - startTripHours > totalHours ? (
                          <>
                            {endTripHours - startTripHours - totalHours}{" "}
                            additional hours.
                            <span style={{ display: "block" }}>
                              Extra charges: $
                              {(endTripHours - startTripHours - totalHours) *
                                50}
                            </span>
                          </>
                        ) : (
                          "No extra hours"
                        )}
                      </Alert>
                    </div>
                    {error && (
                      <Alert className="mt-3" severity="error">
                        {error}
                      </Alert>
                    )}
                    {success && (
                      <Alert className="mt-3" severity="success">
                        {success}
                      </Alert>
                    )}
                  </div>
                </>
              ) : isTripReturned &&
                userData.role === "Boat Owner" &&
                bookingData.renter != auth.currentUser.uid &&
                (inquiryData?.offerData?.captainCheckbox || boatDetails?.boatType === "Fishing Guide" || boatDetails?.boatType === "Fishing Charter" || boatDetails?.boatCaptain === true) ? (
                <div className="d-flex flex-column gap-2 align-items-center">
                  {hasTripInspectionIssue(bookingData) ? (
                    <Alert severity="warning" className="mb-0">
                      Trip returned with reported issues.
                    </Alert>
                  ) : (
                    <Alert severity="success" className="mb-0">
                      Trip has been completed.
                    </Alert>
                  )}
                  {totalTipAmount > 0 && (
                    <Alert severity="info" className="mb-0">
                      Renter gave ${formatCurrency(totalTipAmount / 100)} tip (excluded 3% Stripe charges).
                    </Alert>
                  )}
                  {renderReviewSubmittedMessage()}
                </div>
              ) : (isTripReturned ||
                bookingData.status === "Trip Concluded") &&
                (userData.role === "Renter" ||
                  bookingData.renter === auth.currentUser.uid) ? (
                <div className="d-flex flex-column gap-2 align-items-stretch" style={{ minWidth: "320px", maxWidth: "560px" }}>
                  {bookingData.damageClaim?.status ===
                  "pending_renter_approval" ? (
                    <>
                      <Alert severity="warning" className="mb-0">
                        The boat owner reported{" "}
                        {formatRenterInspectionIssuePhrase(bookingData)} and
                        requested a charge of $
                        {formatCurrency(bookingData.damageClaim.amount)} from
                        your security deposit.
                      </Alert>
                      <p className="mb-0">{bookingData.damageClaim.reason}</p>
                      {bookingData.damageClaim.issueItems?.length > 0 && (
                        <ul className="mb-0 ps-3">
                          {bookingData.damageClaim.issueItems.map((item, index) => (
                            <li key={`${item.question}-${index}`} className="mb-2">
                              {item.question}
                              {item.photos?.length > 0 && (
                                <div className="d-flex flex-wrap gap-2 mt-2">
                                  {item.photos.map((photo, photoIndex) => (
                                    <a
                                      key={`${photo}-${photoIndex}`}
                                      href={formatUploadedAssetUrl(photo)}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      <img
                                        src={formatUploadedAssetUrl(photo)}
                                        alt={item.question}
                                        style={{
                                          width: "80px",
                                          height: "80px",
                                          objectFit: "cover",
                                          borderRadius: "6px",
                                        }}
                                      />
                                    </a>
                                  ))}
                                </div>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                      {bookingData.damageClaim.receiptUrls?.length > 0 && (
                        <div className="d-flex flex-wrap gap-2">
                          {bookingData.damageClaim.receiptUrls.map((receipt, index) => (
                            <a
                              key={`${receipt}-${index}`}
                              href={formatUploadedAssetUrl(receipt)}
                              target="_blank"
                              rel="noreferrer"
                            >
                              View receipt {index + 1}
                            </a>
                          ))}
                        </div>
                      )}
                      <div className="d-flex gap-2 flex-wrap">
                        <Button
                          variant="primary"
                          disabled={paymentProcessing}
                          onClick={handleRenterApproveDamageClaim}
                        >
                          Approve charge
                        </Button>
                        <Button
                          variant="outline-primary"
                          onClick={handleRenterRejectDamageClaim}
                        >
                          Dispute
                        </Button>
                        <Link
                          to={`/dashboard/inbox?userId=${bookingData.owner}`}
                          className="btn btn-link"
                        >
                          Message boat owner
                        </Link>
                      </div>
                    </>
                  ) : bookingData.damageClaim?.status === "approved" ? (
                    <>
                      <Alert severity="info" className="mb-2">
                        You approved the $
                        {formatCurrency(bookingData.damageClaim.amount)} damage
                        claim. The security deposit charge is being processed
                        (only that amount is captured; the rest of the hold is
                        released).
                      </Alert>
                      {paymentDetails?.status !== "Dispute Resolved" &&
                        !isOffPlatformDeposit(paymentDetails) && (
                          <Button
                            className="btn btn-warning"
                            disabled={paymentProcessing}
                            onClick={() =>
                              captureCertainHeldPayment(
                                bookingData.damageClaim.amount,
                                true
                              )
                            }
                          >
                            {paymentProcessing
                              ? "Processing..."
                              : "Retry charge"}
                          </Button>
                        )}
                    </>
                  ) : bookingData.damageClaim?.status === "charged" ? (
                    <Alert severity="success" className="mb-0">
                      {formatRenterInspectionChargeLabel(bookingData)} charge of
                      $
                      {formatCurrency(
                        bookingData.damageCharge ||
                          bookingData.damageClaim.amount
                      )}{" "}
                      was processed from your security deposit.
                    </Alert>
                  ) : hasTripInspectionIssue(bookingData) ? (
                    <Alert severity="warning" className="mb-0">
                      The boat owner reported{" "}
                      {formatRenterInspectionIssuePhrase(bookingData)} and will
                      reach out to you through your{" "}
                      <Link to={`/dashboard/inbox?userId=${bookingData.owner}`}>
                        messaging inbox
                      </Link>
                      , where you can review the details.
                    </Alert>
                  ) : (
                    <Alert severity="success" className="mb-0">
                      Trip has been completed.
                    </Alert>
                  )}
                  {totalTipAmount > 0 ? (
                    <Alert severity="info" className="mb-0">
                      You have given ${formatCurrency(totalTipAmount / 100)} tip to the boat owner.
                    </Alert>
                  ) : (
                    <Link
                      to={`/tip/${dynamicId}`}
                      className="btn btn-success d-flex align-items-center justify-content-center"
                      style={{ height: "44px" }}
                    >
                      Leave a Tip
                    </Link>
                  )}
                  {currentBookingListingReview ? (
                    <Alert severity="info" className="mb-0">
                      You have submitted a review for this booking.
                    </Alert>
                  ) : canReviewListing ? (
                    <Button
                      className="d-flex align-items-center justify-content-center"
                      style={{ height: "44px" }}
                      onClick={() => {
                        setError("");
                        setShowListingReviewModal(true);
                      }}
                    >
                      Write a Review
                    </Button>
                  ) : (
                    <Alert severity="info" className="mb-0">
                      Reviews open once the trip is completed.
                    </Alert>
                  )}
                </div>
              ) : bookingData.status === "Disputed" &&
                userData.role === "Boat Owner" ? (
                <>
                  <Alert severity="warning">Dispute has been initialed.</Alert>
                  {/* <Button onClick={openRequestDisputeModal} className='btn btn-danger'>
                    Request Dispute
                  </Button> */}
                  {/* <Button onClick={openDisputeModal} className='btn btn-danger'>
                    Dispute
                  </Button>
                  <Button onClick={releaseHeldFunds} className='btn btn-success'>
                    All Good
                  </Button> */}
                </>
              ) : bookingData.status === "Disputed" &&
                userData.role === "Renter" ? (
                <>
                  <Alert severity="warning">
                    Boat owner initialed dispute.
                  </Alert>
                </>
              ) : bookingData.status === "Disputed" &&
                userData.role === "Admin" ? (
                <>
                  {/* <Alert severity="warning">Dispute has been initialed.</Alert> */}
                  {/* <Button onClick={openRequestDisputeModal} className='btn btn-danger'>
                    Request Dispute
                  </Button> */}
                  {/* <Button onClick={openDisputeModal} className='btn btn-warning'>
                    Resolve Dispute
                  </Button> */}
                  {/* <Button onClick={releaseHeldFunds} className='btn btn-success'>
                    All Good
                  </Button> } */}
                </>
              ) : bookingData.status === "Trip Concluded" ? (
                // Add your logic for 'Concluded' status here
                <div className="d-flex flex-column gap-2 align-items-center">
                  <Alert severity="success" className="mb-0">
                    Trip has been completed.
                  </Alert>
                  {totalTipAmount > 0 && (
                    <Alert severity="info" className="mb-0">
                      Renter gave ${formatCurrency(totalTipAmount / 100)} tip (excluded 3% Stripe charges).
                    </Alert>
                  )}
                  {renderReviewSubmittedMessage()}
                </div>
              ) : bookingData.status === "cancelled" ? (
                // Add your logic for 'Concluded' status here
                <Alert severity="error">This booking has been cancelled.</Alert>
              ) : (
                "" // Default class for unknown status
              )}
            </div>
          </div>
        </div>
      </div>

      <section>
        <div className="event-text">
          <div className="row">
            <div className="col-lg-6">
              <div className="card mb-4">
                <div className="card-header">
                  <h2 className="card-title">Booking Details</h2>
                </div>
                <div className="card-body mt-2">
                  <div className="row">
                    <div className="col-sm-4">
                      <p className="mb-0">Boat</p>
                    </div>
                    <div className="col-sm-8">
                      <p className="text-muted mb-0">
                        {userData.role === "Admin" ? (
                          <Link to={"/dashboard/listing/" + listingData?.id}>
                            {listingData?.listingTitle}
                          </Link>
                        ) : (
                          listingData?.listingTitle
                        )}{" "}
                      </p>
                    </div>
                  </div>
                  <hr />
                  <div className="row">
                    <div className="col-sm-4">
                      <p className="mb-0">Boat Owner</p>
                    </div>
                    <div className="col-sm-8">
                      {console.log("bookingData.....ttt", bookingData)}
                      <p className="text-muted mb-0">
                        {userData.role === "Admin" ? (
                          <Link to={"/dashboard/user/" + bookingData.owner}>
                            {ownerData.firstName} {ownerData.lastName}{" "}
                          </Link>
                        ) : (
                          ownerData.firstName + " " + ownerData.lastName
                        )}{" "}
                      </p>
                    </div>
                  </div>
                  <hr />
                  <div className="row">
                    <div className="col-sm-4">
                      <p className="mb-0">Renter</p>
                    </div>
                    <div className="col-sm-8">
                      <p className="text-muted mb-0">
                        {userData.role === "Admin" ? (
                          <Link to={"/dashboard/user/" + bookingData.renter}>
                            {renterData.firstName} {renterData.lastName}
                          </Link>
                        ) : userData.role === "Boat Owner" ? (
                          <Link to={"/dashboard/renter/" + bookingData.renter}>
                            {renterData.firstName} {renterData.lastName}
                          </Link>
                        ) : (
                          renterData.firstName + " " + renterData.lastName
                        )}
                      </p>
                    </div>
                  </div>
                  <hr />
                  {passengerNames.length > 0 && (
                    <>
                      <div className="row">
                        <div className="col-sm-4">
                          <p className="mb-0" style={{ fontWeight: "600" }}>
                            Passengers ({passengerNames.length}/{getRequiredPassengerWaivers(bookingForWaivers, boatDetails)})
                          </p>
                        </div>
                        <div className="col-sm-8">
                          <ol style={{ paddingLeft: "18px", marginBottom: "0" }}>
                            {passengerNames.map((passenger, index) => (
                              <li key={index} className="text-muted mb-1" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                {userData.role === "Admin" && passenger.waiverData && passenger.waiverData.length > 0 && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    style={{ fontSize: "11px", padding: "2px 8px", lineHeight: "1.4", marginRight: "4px" }}
                                    onClick={() => handleViewWaiver(passenger)}
                                  >
                                    View Waiver
                                  </button>
                                )}
                                <span>
                                  {passenger.name}{" "}
                                  <span style={{ fontSize: "11px", color: "#999" }}>
                                    ({passenger.type})
                                  </span>
                                </span>
                              </li>
                            ))}
                          </ol>
                          {passengerNames.length <
                            getRequiredPassengerWaivers(bookingForWaivers, boatDetails) &&
                            !arePassengerWaiversComplete(bookingForWaivers, boatDetails) && (
                              <p style={{ color: "red", fontSize: "12px", marginTop: "4px", marginBottom: "0" }}>
                                ⚠ {getRequiredPassengerWaivers(bookingForWaivers, boatDetails) - passengerNames.length} waiver(s) still pending
                              </p>
                            )}
                        </div>
                      </div>
                      <hr />
                    </>
                  )}
                  {userData.role === "Admin" && (
                    <>
                      <div className="row">
                        <div className="col-sm-4">
                          <p className="mb-0">Renter Email</p>
                        </div>
                        <div className="col-sm-8">
                          <p className="text-muted mb-0">{renterData.email}</p>
                        </div>
                      </div>
                      <hr />
                    </>
                  )}

                  <div className="row">
                    <div className="col-sm-4">
                      <p className="mb-0">Time Stamp</p>
                    </div>
                    <div className="col-sm-8">
                      <p className="text-muted mb-0">{timeStamp}</p>
                    </div>
                  </div>
                  <hr />
                  {userData?.role === "Admin" && (
                    <>
                      <div className="row">
                        <div className="col-sm-4">
                          <p className="mb-0">Booking Status</p>
                        </div>
                        <div className="col-sm-8">
                          <p className="mb-0">
                            {(() => {
                              const status = bookingData?.status?.toLowerCase() || "pending";
                              switch (status) {
                                case "sent":
                                  return <span style={{ color: "green", fontWeight: "bold" }}>✓ Offer Sent</span>;
                                case "accepted":
                                  return <span style={{ color: "#008000", fontWeight: "bold" }}>✓ Offer Accepted</span>;
                                case "waiver pending":
                                  return <span style={{ color: "#FF8C00", fontWeight: "bold" }}>⚠️ Waiver Pending</span>;
                                case "waiver filled":
                                  return <span style={{ color: "#FF8C00", fontWeight: "bold" }}>📝 Waiver Filled</span>;
                                case "tripstarted":
                                  return <span style={{ color: "#4169E1", fontWeight: "bold" }}>🚤 Trip Started</span>;
                                case "completed":
                                case "trip completed":
                                case "trip returned":
                                  return <span style={{ color: "#313130", fontWeight: "bold" }}>↩️ Trip Returned</span>;
                                case "trip concluded":
                                  return <span style={{ color: "#008000", fontWeight: "bold" }}>🏁 Trip Concluded</span>;
                                case "cancelled":
                                  return <span style={{ color: "#FF0000", fontWeight: "bold" }}>❌ Cancelled</span>;
                                case "rejected":
                                  return <span style={{ color: "#8B0000", fontWeight: "bold" }}>❌ Rejected</span>;
                                case "payment pending":
                                  return <span style={{ color: "#FFA500", fontWeight: "bold" }}>💰 Payment Pending</span>;
                                case "paid":
                                  return <span style={{ color: "#FFA500", fontWeight: "bold" }}>💰 Paid</span>;
                                case "in progress":
                                  return <span style={{ color: "#1E90FF", fontWeight: "bold" }}>🔄 In Progress</span>;
                                default:
                                  return <span style={{ color: "#d4a017", fontWeight: "bold" }}>⌛ Offer Pending</span>;
                              }
                            })()}
                          </p>
                        </div>
                      </div>
                      <hr />
                    </>
                  )}

                  {/* Dock / Marina */}
                  {boatDetails?.fishingGuideDetails?.businessInfo?.marinaName && (
                    <>
                      <div className="row">
                        <div className="col-sm-4">
                          <p className="mb-0">Dock / Marina</p>
                        </div>
                        <div className="col-sm-8">
                          <p className="text-muted mb-0">{boatDetails.fishingGuideDetails.businessInfo.marinaName}</p>
                        </div>
                      </div>
                      <hr />
                    </>
                  )}

                  {/* Meeting Instructions */}
                  {boatDetails?.fishingGuideDetails?.businessInfo?.meetingInstructions && (
                    <>
                      <div className="row">
                        <div className="col-sm-4">
                          <p className="mb-0">Meeting Instructions</p>
                        </div>
                        <div className="col-sm-8">
                          <p className="text-muted mb-0">{boatDetails.fishingGuideDetails.businessInfo.meetingInstructions}</p>
                        </div>
                      </div>
                      <hr />
                    </>
                  )}

                  {/* Parking / Gate Instructions */}
                  {boatDetails?.fishingGuideDetails?.businessInfo?.parkingInstructions && (
                    <>
                      <div className="row">
                        <div className="col-sm-4">
                          <p className="mb-0">Parking / Gate Instructions</p>
                        </div>
                        <div className="col-sm-8">
                          <p className="text-muted mb-0">{boatDetails.fishingGuideDetails.businessInfo.parkingInstructions}</p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {bookingData && offer && (
                <>
                  <div className="card mb-4">
                    <div className="card-header">
                      <h2 className="card-title">Offer Details</h2>
                    </div>
                    <div className="card-body mt-2">
                      {userData?.role === "Admin" && (
                        <>
                          <div className="row">
                            <div className="col-sm-4">
                              <p className="mb-0">Offer Sent Time</p>
                            </div>
                            <div className="col-sm-8">
                              <p className="text-muted mb-0">
                                {inquiryData?.timestamp
                                  ? formatDateTime(inquiryData.timestamp)
                                  : "N/A"}
                              </p>
                            </div>
                          </div>
                          <hr />
                        </>
                      )}
                      <div className="row">
                        <div className="col-sm-4">
                          <p className="mb-0">Offered Price</p>
                        </div>
                        <div className="col-sm-8">
                          <p className="text-muted mb-0">${formattedOfferPrice}</p>
                        </div>
                      </div>
                      <hr />
                      <div className="row">
                        <div className="col-sm-4">
                          <p className="mb-0">Pickup Date Time</p>
                        </div>
                        <div className="col-sm-8">
                          <p className="text-muted mb-0">
                            {offeredFormattedDateTime}
                          </p>
                          {/* <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                    value={offerStartDateTime}
                    onChange={(value) => setOfferStartDateTime(value)}
                    />
                </LocalizationProvider> */}
                        </div>
                      </div>
                      <hr />
                      <div className="row">
                        <div className="col-sm-4">
                          <p className="mb-0">Duration</p>
                        </div>
                        <div className="col-sm-8">
                          <p className="text-muted mb-0">{offer?.duration}</p>
                          {console.log(
                            "offer...............---------========",
                            offer
                          )}
                        </div>
                      </div>
                      <hr />
                      <div className="row">
                        <div className="col-sm-4">
                          <p className="mb-0">Return Date Time</p>
                        </div>
                        <div className="col-sm-8">
                          <p className="text-muted mb-0">
                            {offer?.offeredDate &&
                              offer?.offeredTime &&
                              (offer?.offeredHours || offer?.overnight)
                              ? calculateReturnDateTime(
                                offer.offeredDate,
                                offer.offeredTime,
                                offer.offeredHours,
                                offer.overnight
                              )?.fullDateTime || ""
                              : ""}
                          </p>
                        </div>
                      </div>
                      {userData?.role === "Admin" && (
                        <div className="row">
                          <div className="col-sm-4">
                            <p className="mb-0">Customer Placed Date</p>
                          </div>
                          <div className="col-sm-8">
                            <p className="text-muted mb-0">
                              {inquiryData?.timestamp
                                ? formatDateTime(inquiryData.timestamp)
                                : "Pending"}
                            </p>
                          </div>
                        </div>
                      )}
                      <hr />
                      {userData?.role === "Admin" && (
                        <>
                          <div className="row">
                            <div className="col-sm-4">
                              <p className="mb-0">Offer Accepted Date</p>
                            </div>
                            <div className="col-sm-8">
                              <p className="text-muted mb-0">
                                {bookingData?.timestamp
                                  ? formatDateTime(bookingData.timestamp)
                                  : "N/A"}
                              </p>
                            </div>
                          </div>
                          <hr />
                        </>
                      )}
                      {pickUpLocation ? (
                        <div className="row">
                          <div className="col-sm-4">
                            <p className="mb-0">Pickup Location</p>
                          </div>
                          <div className="col-sm-8">
                            <p className="text-muted mb-0">{pickUpLocation}</p>
                            {console.log("pickUpLocation", pickUpLocation)}
                          </div>
                        </div>
                      ) : (
                        ""
                      )}
                      <hr />
                      {dropOffLocation ? (
                        <div className="row">
                          <div className="col-sm-4">
                            <p className="mb-0">Dropoff Location</p>
                          </div>
                          <div className="col-sm-8">
                            <p className="text-muted mb-0">{dropOffLocation}</p>
                          </div>
                        </div>
                      ) : (
                        ""
                      )}



                      {/* Cancellation Policy */}
                      {(boatDetails?.fishingGuideDetails?.policies?.cancellation || offer?.freeCancellation || true) && (
                        <>
                          <hr />
                          <div className="row">
                            <div className="col-sm-4">
                              <p className="mb-0">Cancellation Policy</p>
                            </div>
                            <div className="col-sm-8">
                              <CancellationPolicyDisplay
                                policyType={boatDetails?.fishingGuideDetails?.policies?.cancellation}
                                freeCancellation={offer?.freeCancellation}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="col-lg-6">
              {bookingData && tripDetails && (
                <>
                  <div className="card mb-4">
                    <div className="card-header">
                      <h2 className="card-title">Trip Details</h2>
                    </div>
                    <div className="card-body mt-2">
                      <div className="row">
                        <div className="col-sm-4">
                          <p className="mb-0">Trip Start Date Time</p>
                        </div>
                        <div className="col-sm-8">
                          <p className="text-muted mb-0">
                            {formatTimestamp(
                              hasTripStarted ? tripDetails.startTimestamp : null
                            )}
                          </p>
                        </div>
                      </div>
                      <hr />
                      <div className="row">
                        <div className="col-sm-4">
                          <p className="mb-0">Trip End Date Time</p>
                        </div>
                        <div className="col-sm-8">
                          <p className="text-muted mb-0">
                            {formatTimestamp(tripDetails.endTimestamp)}
                          </p>
                        </div>
                      </div>
                      <hr />
                      <div className="row">
                        <div className="col-sm-4">
                          <p className="mb-0">Duration</p>
                        </div>
                        <div className="col-sm-8">
                          <p className="text-muted mb-0">
                            {calculateDuration(
                              hasTripStarted ? tripDetails.startTimestamp : null,
                              tripDetails.endTimestamp
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {bookingData && inquiry && (
                console.log("inquiryyyyyyyy", inquiry),
                <>
                  <div className="event-text card mb-4">
                    <div className="card-header">
                      <h2 className="card-title">Inquiry Details</h2>
                    </div>
                    <div className="card-body mt-2">
                      <div className="row">
                        <div className="col-sm-4">
                          <p className="mb-0">Passengers</p>
                        </div>
                        <div className="col-sm-8">
                         <p className="text-muted mb-0">
                            {formatPassengerSummary(inquiry.inquiryData)}
                          </p>
                        </div>
                      </div>
                      <hr />
                      <div className="row">
                        <div className="col-sm-4">
                          <p className="mb-0">Prefered Date</p>
                        </div>
                        <div className="col-sm-8">
                          <p className="text-muted mb-0">
                            {inquiry?.inquiryData?.prefferedDate}
                          </p>
                        </div>
                      </div>
                      <hr />
                      <div className="row">
                        <div className="col-sm-4">
                          <p className="mb-0">Prefered Time</p>
                        </div>
                        <div className="col-sm-8">
                          <p className="text-muted mb-0">
                            {inquiry?.inquiryData?.prefferedTime}
                          </p>
                        </div>
                      </div>
                      <hr />
                      {inquiry.otherDateOption1 ? (
                        <>
                          <div className="row">
                            <div className="col-sm-4">
                              <p className="mb-0">Prefered Date 2</p>
                            </div>
                            <div className="col-sm-8">
                              <p className="text-muted mb-0">
                                {inquiry.otherDateOption1}
                              </p>
                            </div>
                          </div>
                          <hr />{" "}
                        </>
                      ) : (
                        ""
                      )}
                      {inquiry.otherDateOption2 ? (
                        <>
                          <div className="row">
                            <div className="col-sm-4">
                              <p className="mb-0">Prefered Date 3</p>
                            </div>
                            <div className="col-sm-8">
                              <p className="text-muted mb-0">
                                {inquiry.otherDateOption2}
                              </p>
                            </div>
                          </div>
                          <hr />{" "}
                        </>
                      ) : (
                        ""
                      )}
                      <div className="row">
                        <div className="col-sm-4">
                          <p className="mb-0">Duration</p>
                        </div>
                        <div className="col-sm-8">
                          <div
                            className="text-muted mb-0 d-flex"
                            style={{ gap: "5px" }}
                          >
                            {inquiry?.inquiryData?.overnight > 0 && (
                              <p className="mb-1">
                                {inquiry?.inquiryData?.overnight} Nights{" "}
                              </p>
                            )}
                            {inquiry?.inquiryData?.sameDayHours > 0 && (
                              <p className="mb-1">
                                {inquiry?.inquiryData?.sameDayHours} Hours{" "}
                              </p>
                            )}
                            {inquiry?.inquiryData?.sameDayMinutes > 0 && (
                              <p className="mb-1">
                                {inquiry?.inquiryData?.sameDayMinutes} Minutes{" "}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <hr />
                      <div className="row">
                        <div className="col-sm-4">
                          <p className="mb-0">Comments</p>
                        </div>
                        <div className="col-sm-8">
                          <p className="text-muted mb-0">
                            {inquiry?.inquiryData?.comments}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {paymentDetails !== null &&
                inquiryData &&
                bookingData.owner === auth.currentUser.uid &&
                boatDetails?.boatType !== "Fishing Charter" &&
                boatDetails?.boatType !== "Fishing Guide" && (
                  <>
                    <div className="card mb-4">
                      <div className="card-header">
                        <h2 className="card-title">Payment Details</h2>
                      </div>
                      <div className="card-body mt-2">
                        <div className="row">
                          <div className="col-sm-4">
                            <p className="mb-0">Booking Amount</p>
                          </div>
                          <div className="col-sm-8">
                            <p className="text-muted mb-0">
                              $
                              {offer
                                ? Number(offer.offerPrice).toFixed(2)
                                : ""}
                            </p>

                            <p className="mb-0">
                              {paymentDetails?.advanceBooking &&
                                paymentDetails?.remainingAmount > 0
                                ? "$" +
                                (paymentDetails?.totalAmount / 100 -
                                  paymentDetails?.advancePaid / 100) +
                                " remaining will be charged from the customer 48 prior to start date"
                                : ""}
                            </p>
                            {/* <p className="mb-0">
                              {paymentDetails?.firstRentalPayment &&
                                paymentDetails?.adminFirstTimeCut &&
                                !paymentDetails?.referrer
                                ? "$" +
                                paymentDetails?.adminFirstTimeCut +
                                " have been deducted for the first completed rental."
                                : ""}
                            </p>
                            <p className="mb-0">
                              {paymentDetails?.firstRentalPayment &&
                                !paymentDetails?.adminFirstTimeCut &&
                                !paymentDetails?.referrer &&
                                !paymentDetails?.referAmount
                                ? "Admin fee have been deducted for the first completed rental."
                                : ""}
                            </p>
                            <p className="mb-0">
                              {paymentDetails?.firstRentalPayment &&
                                !paymentDetails?.adminFirstTimeCut &&
                                paymentDetails?.referrer &&
                                paymentDetails?.referAmount
                                ? "$50 have been deducted for the first completed rental."
                                : ""}
                            </p>
                            <p className="mb-0">
                              {paymentDetails?.firstRentalPayment &&
                                paymentDetails?.adminFirstTimeCut &&
                                paymentDetails?.referrer &&
                                paymentDetails?.referAmount
                                ? "$" +
                                paymentDetails?.adminFirstTimeCut / 100 +
                                " have been deducted for the first completed rental."
                                : ""}
                            </p> */}
                          </div>
                        </div>
                        {/* ////////////// */}
                        {paymentDetails &&
                          paymentDetails?.advancePaid &&
                          paymentDetails?.advancePaid > 0 && (
                            <>
                              <div className="row">
                                <div className="col-sm-4">
                                  <p className="mb-0">Deposit Amount Paid</p>
                                </div>
                                <div className="col-sm-8">
                                  <p className="text-muted mb-0">
                                    ${paymentDetails?.advancePaid / 100}{" "}
                                  </p>
                                </div>
                              </div>
                            </>
                          )}
                        {offer && renderCommissionRateDetails(offer.offerPrice)}
                        {userData.role === "Boat Owner" && (
                          <div className="row">
                            <div className="col-sm-4">
                              <p className="mb-0">Boat Owner Payout</p>
                            </div>
                            <div className="col-sm-8">
                              <p className="text-muted mb-0">
                                $
                                {offer
                                  ? calculateOwnerAmountFromOffer(offer.offerPrice)
                                  : ""}
                              </p>
                            </div>
                          </div>
                        )}

                        {renderFirstRentalFeeRow(
                          "(deducted from your payout for first completed rental)"
                        )}

                        {renderClaimChargePaymentRows()}

                        {/* /////////// */}
                      </div>
                    </div>
                  </>
                )}
{
  console.log('paymentDetails....', paymentDetails)
}
              {paymentDetails !== null &&
                inquiryData &&
                ((bookingData.renter === auth.currentUser.uid &&
                  bookingData.owner !== auth.currentUser.uid) ||
                  (bookingData.owner === auth.currentUser.uid &&
                    (boatDetails?.boatType === "Fishing Charter" ||
                      boatDetails?.boatType === "Fishing Guide"))) && (
                  <>
                    {console.log("renterrrrrrrrrrr")}
                    <div className="card mb-4">
                      <div className="card-header">
                        <h2 className="card-title">Payment Details</h2>
                      </div>
                      <div className="card-body mt-2">
                        {formattedOfferPrice && (
                          <div className="row">
                            <div className="col-sm-4">
                              <p className="mb-0">Grand Total</p>
                            </div>
                            <div className="col-sm-8">
                              <p className="mb-0 text-muted">
                                $
                                {(Number(offerPriceAsNumber) +
                                  calculateRenterFee(offerPriceAsNumber, boatDetails) +
                                  (paymentDetails && paymentDetails.totalTax > 0 ? (8.25 / 100) * offerPriceAsNumber : 0) +
                                  chargedClaimTotal).toFixed(2)}
                              </p>
                            </div>
                          </div>
                        )}

                        <hr />

                        {formattedOfferPrice && (
                          <div className="row">
                            <div className="col-sm-4">
                              <p className="mb-0">5% platform Fees</p>
                            </div>
                            <div className="col-sm-8">
                              <p className="mb-0 text-muted">
                                {calculateRenterFee(offerPriceAsNumber, boatDetails) > 0
                                  ? <>${calculateRenterFee(offerPriceAsNumber, boatDetails).toFixed(2)}{" "}
                                    (5% of ${formattedOfferPrice})</>
                                  : "$0.00"}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* BBR Commission is owner-side only; renters should not see it */}
                        {offer &&
                          bookingData.owner === auth.currentUser.uid &&
                          renderCommissionRateDetails(offer.offerPrice)}

                        {/* Owner Amount section - only for boat owners */}
                        {bookingData.owner === auth.currentUser.uid && (
                          <>
                            <div className="row">
                              <div className="col-sm-4">
                                <p className="mb-0">
                                  Owner Amount ({getOwnerSharePercentage()}% of Offered Price)
                                </p>
                              </div>
                              <div className="col-sm-8">
                                <p className="text-muted mb-0">
                                  $
                                  {offer && calculateOwnerAmountFromOffer(offer?.offerPrice)}
                                </p>
                              </div>
                            </div>
                            {renderFirstRentalFeeRow(
                              "(deducted from your payout for first completed rental)"
                            )}
                          </>
                        )}
                        
                        <hr />

                        {formattedOfferPrice && (
                          <div className="row">
                            <div className="col-sm-4">
                              <p className="mb-0">Tax</p>
                            </div>
                            <div className="col-sm-8">
                              <p className="mb-0 text-muted">
                                {paymentDetails && paymentDetails.totalTax > 0
                                  ? <>
                                    ${formatCurrency((8.25 / 100) * offerPriceAsNumber)}{" "}
                                    {`(8.25% of $${formattedOfferPrice})`}
                                  </>
                                  : "$0.00 (No tax applicable)"}
                              </p>
                            </div>
                          </div>
                        )}

                        {paymentDetails?.charterDeposit && (
                          <>
                            <hr />
                            <div className="row">
                              <div className="col-sm-4">
                                <p className="mb-0">Deposit Paid ({paymentDetails.charterDepositPercentage}%)</p>
                              </div>
                              <div className="col-sm-8">
                                <p className="mb-0 text-muted">
                                  ${paymentDetails.charterDepositPaid ? (paymentDetails.charterDepositPaid / 100).toFixed(2) : "0.00"} + tax/fees (if applicable) paid online
                                </p>
                              </div>
                            </div>
                            <hr />
                            <div className="row">
                              <div className="col-sm-4">
                                {(() => {
                                  let actualRemaining = ((paymentDetails.totalAmount - paymentDetails.charterDepositPaid) / 100);
                                  if (paymentDetails.charterRemainingBalance !== undefined) {
                                    actualRemaining = paymentDetails.charterRemainingBalance / 100;
                                  } else if (paymentDetails.remainingAmount !== undefined) {
                                    actualRemaining = paymentDetails.remainingAmount / 100;
                                  } else if (paymentDetails.charterCashBalance !== undefined) {
                                    actualRemaining = paymentDetails.charterCashBalance / 100;
                                  }

                                  if (actualRemaining < 0) return <p className="mb-0">Refund Amount</p>;
                                  return <p className="mb-0">{paymentDetails.collectBalanceAtMeetup ? "Cash Balance Due" : "Remaining Balance"}</p>;
                                })()}
                              </div>
                              <div className="col-sm-8">
                                {(() => {
                                  let actualRemaining = ((paymentDetails.totalAmount - paymentDetails.charterDepositPaid) / 100);
                                  if (paymentDetails.charterRemainingBalance !== undefined) {
                                    actualRemaining = paymentDetails.charterRemainingBalance / 100;
                                  } else if (paymentDetails.remainingAmount !== undefined) {
                                    actualRemaining = paymentDetails.remainingAmount / 100;
                                  } else if (paymentDetails.charterCashBalance !== undefined) {
                                    actualRemaining = paymentDetails.charterCashBalance / 100;
                                  }

                                  if (actualRemaining < 0) {
                                    return (
                                      <p className="mb-0 text-success">
                                        ${Math.abs(actualRemaining).toFixed(2)}
                                      </p>
                                    );
                                  } else if (actualRemaining === 0) {
                                    return (
                                      <p className="mb-0 text-muted">
                                        $0.00
                                      </p>
                                    );
                                  } else if (paymentDetails.collectBalanceAtMeetup) {
                                    return (
                                      <p className="mb-0" style={{ color: "#dc3545" }}>
                                        ${actualRemaining.toFixed(2)} to be collected by boat owner
                                      </p>
                                    );
                                  } else {
                                    return (
                                      <p className="mb-0" style={{ color: "#e67e22" }}>
                                        ${actualRemaining.toFixed(2)} will be collected by BBR 48 hours prior to the trip
                                      </p>
                                    );
                                  }
                                })()}
                              </div>
                            </div>
                          </>
                        )}

                        {renderClaimChargePaymentRows()}
                      </div>
                    </div>
                  </>
                )}

              {/* {paymentDetails !== null &&
                inquiryData &&
                bookingData.renter === auth.currentUser.uid &&
                bookingData.owner !== auth.currentUser.uid &&
                bookingData.status != "pending" &&
                boatDetails?.boatType !== "Fishing Charter" &&
                boatDetails?.boatType !== "Fishing Guide" && (
                  <>
                    <div className="card mb-4">
                      <div className="card-header">
                        <h2 className="card-title">Payment Details3</h2>
                      </div>
                      <div className="card-body mt-2">
                        <div className="row">
                          <div className="col-sm-4">
                            <p className="mb-0">Amount</p>
                          </div>
                          <div className="col-sm-8">
                            {paymentDetails.cutPercentage &&
                              paymentDetails?.advanceBooking == 1 && (
                                <p className="text-muted mb-0">
                                  $
                                  {paymentDetails &&
                                    paymentDetails?.remainingAmount != 0
                                    ? paymentDetails.advancePaid / 100
                                    : paymentDetails.totalAmount / 100}
                                </p>
                              )}
                            {paymentDetails.cutPercentage &&
                              !paymentDetails?.advanceBooking && (
                                <p className="text-muted mb-0">
                                  $
                                  {paymentDetails
                                    ? paymentDetails.totalAmount / 100
                                    : ""}
                                </p>
                              )}
                            <p className="mb-0">
                              {paymentDetails?.advanceBooking == 1 &&
                                paymentDetails?.remainingAmount > 0
                                ? "$" +
                                paymentDetails?.remainingAmount / 100 +
                                " remaining will be charged 2 days prior to start date"
                                : ""}
                            </p>
                            <p className="mb-0">
                              {paymentDetails?.firstRentalPayment &&
                                paymentDetails?.adminFirstTimeCut &&
                                !paymentDetails?.referrer
                                ? "$" +
                                paymentDetails?.adminFirstTimeCut / 100 +
                                " have been deducted for the first completed rental."
                                : ""}
                            </p>
                            <p className="mb-0">
                              {paymentDetails?.firstRentalPayment &&
                                !paymentDetails?.adminFirstTimeCut &&
                                !paymentDetails?.referrer &&
                                !paymentDetails?.referAmount
                                ? "$100 have been deducted for the first completed rental. If the amount is less than $200, then 50% will be deducted."
                                : ""}
                            </p>
                            <p className="mb-0">
                              {paymentDetails?.firstRentalPayment &&
                                !paymentDetails?.adminFirstTimeCut &&
                                paymentDetails?.referrer &&
                                paymentDetails?.referAmount
                                ? "$50 have been deducted for the first completed rental."
                                : ""}
                            </p>
                            <p className="mb-0">
                              {paymentDetails?.firstRentalPayment &&
                                paymentDetails?.adminFirstTimeCut &&
                                paymentDetails?.referrer &&
                                paymentDetails?.referAmount
                                ? "$" +
                                paymentDetails?.adminFirstTimeCut / 100 +
                                " have been deducted for the first completed rental."
                                : ""}
                            </p>
                          </div>
                        </div>

                        {paymentDetails?.charterDeposit && (
                          <>
                            <hr />
                            <div className="row">
                              <div className="col-sm-4">
                                <p className="mb-0">Deposit Paid ({paymentDetails.charterDepositPercentage}%)</p>
                              </div>
                              <div className="col-sm-8">
                                <p className="text-muted mb-0">
                                  ${paymentDetails.charterDepositPaid ? (paymentDetails.charterDepositPaid / 100).toFixed(2) : "0.00"} paid online
                                </p>
                              </div>
                            </div>
                            <hr />
                            <div className="row">
                              <div className="col-sm-4">
                                <p className="mb-0">Balance Due</p>
                              </div>
                              <div className="col-sm-8">
                                <p className="mb-0" style={{color: "#dc3545"}}>
                                  ${paymentDetails.charterCashBalance ? (paymentDetails.charterCashBalance / 100).toFixed(2) : "0.00"} to be collected by boat owner
                                </p>
                              </div>
                            </div>
                          </>
                        )}

                      </div>
                    </div>
                  </>
                )} */}
              {paymentDetails !== null &&
                inquiryData &&
                userData.role === "Admin" &&
                bookingData.status != "pending" && (
                  <>
                    <div className="card mb-4">
                      <div className="card-header">
                        <h2 className="card-title">Payment Details</h2>
                      </div>

                      <div className="card-body mt-2">
                        <div className="row">
                          <div className="col-sm-4">
                            <p className="mb-0">
                              Total Amount{paymentDetails && paymentDetails.totalTax > 0 ? " (Including Sales Tax)" : ""}
                            </p>
                          </div>
                          <div className="col-sm-8">
                            <p className="text-muted mb-0">
                              $
                              {paymentDetails
                                ? paymentDetails.totalAmount / 100
                                : ""}
                            </p>
                          </div>
                        </div>
                        <hr />
                        {offer && renderCommissionRateDetails(offer.offerPrice)}
                        <div className="row">
                          <div className="col-sm-4">
                            <p className="mb-0">
                              Owner Amount ({getOwnerSharePercentage()}% of Offered Price)
                            </p>
                          </div>
                          <div className="col-sm-8">
                            <p className="text-muted mb-0">
                              $
                              {offer && calculateOwnerAmountFromOffer(offer?.offerPrice)}
                            </p>
                          </div>
                        </div>
                        {renderFirstRentalFeeRow(
                          "(charged from boat owner for first completed rental)"
                        )}
                        <hr />
                        {paymentDetails && paymentDetails.referAmount > 0 ? (
                          <>
                            <div className="row">
                              <div className="col-sm-4">
                                <p className="mb-0">Referrer Amount</p>
                              </div>
                              <div className="col-sm-8">
                                <p className="text-muted mb-0">
                                  $
                                  {paymentDetails
                                    ? paymentDetails.referAmount / 100
                                    : ""}
                                </p>
                              </div>
                            </div>
                            <hr />
                          </>
                        ) : (
                          ""
                        )}

                        <div className="row">
                          <div className="col-sm-4">
                            <p className="mb-0">Total Sales Tax{paymentDetails && paymentDetails.totalTax > 0 ? " (8.25%)" : ""}</p>
                          </div>
                          <div className="col-sm-8">
                            <p className="text-muted mb-0">
                              $
                              {paymentDetails
                                ? paymentDetails.totalTax / 100
                                : "0.00"}
                              {paymentDetails && paymentDetails.totalTax === 0 ? " (No tax applicable)" : ""}
                            </p>
                          </div>
                        </div>

                        {paymentDetails?.charterDeposit && (
                          <>
                            <hr />
                            <div className="row">
                              <div className="col-sm-4">
                                <p className="mb-0">Charter Deposit ({paymentDetails.charterDepositPercentage}%)</p>
                              </div>
                              <div className="col-sm-8">
                                <p className="text-muted mb-0">
                                  ${paymentDetails.charterDepositPaid ? (paymentDetails.charterDepositPaid / 100).toFixed(2) : "0.00"} + tax/fees (if applicable) paid online
                                </p>
                              </div>
                            </div>
                            <hr />
                            <div className="row">
                              <div className="col-sm-4">
                                <p className="mb-0">{paymentDetails.collectBalanceAtMeetup ? "Cash Balance Due to Owner" : "Remaining Balance (BBR Collects)"}</p>
                              </div>
                              <div className="col-sm-8">
                                {paymentDetails.collectBalanceAtMeetup ? (
                                  <p className="text-muted mb-0" style={{ color: "#dc3545" }}>
                                    ${(paymentDetails.charterRemainingBalance ? (paymentDetails.charterRemainingBalance / 100) : paymentDetails.charterCashBalance ? (paymentDetails.charterCashBalance / 100) : ((paymentDetails.totalAmount - paymentDetails.charterDepositPaid) / 100)).toFixed(2)} to be paid in cash
                                  </p>
                                ) : (
                                  <p className="text-muted mb-0" style={{ color: "#e67e22" }}>
                                    ${(paymentDetails.charterRemainingBalance ? (paymentDetails.charterRemainingBalance / 100) : paymentDetails.remainingAmount ? (paymentDetails.remainingAmount / 100) : ((paymentDetails.totalAmount - paymentDetails.charterDepositPaid) / 100)).toFixed(2)} will be collected 48 hours prior to trip
                                  </p>
                                )}
                              </div>
                            </div>
                          </>
                        )}

                        <hr />
                        <div className="row">
                          <div className="col-sm-4">
                            <p className="mb-0">5% platform Fees</p>
                          </div>
                          <div className="col-sm-8">
                            <p className="text-muted mb-0">
                              {calculateRenterFee(offerPriceAsNumber, boatDetails) > 0
                                ? <>${calculateRenterFee(offerPriceAsNumber, boatDetails).toFixed(2)}{" "}
                                  (5% of ${formattedOfferPrice})</>
                                : "$0.00"}
                            </p>
                          </div>
                        </div>
                        <hr />
                      </div>
                    </div>
                  </>
                )}
            </div>
          </div>
          {tripDetails &&
            tripDetails.waivers &&
            Object.keys(tripDetails.waivers).length > 0 && (
              <WaiversCard waivers={mergedWaivers} />
            )}

          {/* Booking History Section */}
          {bookingData && bookingData.history && (
            <div className="card mb-4 mt-4">
              <div className="card-header bg-white">
                <h5 className="card-title fw-bold mb-0">Booking History</h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-striped mb-0" style={{ fontSize: '14px' }}>
                    <thead className="bg-light">
                      <tr>
                        <th style={{ width: '25%' }}>Date</th>
                        <th style={{ width: '25%' }}>Updated By</th>
                        <th>Changes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.values(bookingData.history)
                        .sort((a, b) => b.timestamp - a.timestamp)
                        .map((entry, index) => (
                          <tr key={index}>
                            <td>{formatTimestamp(entry.timestamp)}</td>
                            <td>{entry.updatedBy}</td>
                            <td>
                              <ul className="mb-0 ps-3">
                                {entry.changes && entry.changes.map((change, i) => (
                                  <li key={i}>
                                    <strong>{change.field}:</strong> <span className="text-danger">{typeof change.old === 'object' ? JSON.stringify(change.old) : change.old}</span> &rarr; <span className="text-success">{typeof change.new === 'object' ? JSON.stringify(change.new) : change.new}</span>
                                  </li>
                                ))}
                              </ul>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <Modal
        centered
        show={isRequestDisuputeModalOpen}
        onHide={closeRequestDisputeModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>File A Dispute</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col style={{ gap: "5px" }}>
              <h6
                className="mt-2"
                style={{ fontSize: "13px", textAlign: "center" }}
              >
                Disputes may take up to several days to process.
              </h6>
              <Form.Label>Dispute Amount</Form.Label>
              <CurrencyInput
                id="disputeAmount"
                name="disputeAmount"
                placeholder="Enter Price"
                defaultValue={0}
                decimalsLimit={2}
                value={disputeAmount}
                prefix="$"
                onValueChange={(value) => setDisputeAmount(value)}
                className="form-control"
              />
            </Col>
          </Row>

          <Row>
            <Col>
              <Form.Label className="mt-3">Reason</Form.Label>
              <TextField
                style={{ width: "100%" }}
                multiline
                rows={4}
                id="outlined-basic"
                value={disputeReason}
                onChange={(event) => setDisputeReason(event.target.value)}
                label="Dispute Reason"
                variant="outlined"
              />
            </Col>
          </Row>
          <Row className="d-flex justify-content-center mt-3">
            {error != "" && (
              <Alert style={{ width: "96%" }} severity="error">
                {error}.
              </Alert>
            )}
          </Row>
          {/* Additional content goes here */}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeRequestDisputeModal}>
            Close
          </Button>
          <Button variant="primary" onClick={sendDispute}>
            Submit
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal centered show={isDisuputeModalOpen} onHide={closeDisputeModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            {canDirectChargeFromHold && !claimRequiresRenterApproval
              ? "Charge from Security Deposit"
              : "Submit Damage Claim"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col style={{ gap: "5px" }}>
              {isOffPlatformDeposit(paymentDetails) ? (
                <Alert severity="info">
                  Security deposit was collected off-platform (cash/Venmo). There
                  is no card hold to charge or release.
                </Alert>
              ) : (
                <>
                  <h6
                    className="mt-2"
                    style={{ fontSize: "14px", textAlign: "center" }}
                  >
                    Security Deposit: {securityAmount}
                  </h6>
                  {getInspectionIssueItemsFromBooking(bookingData).length > 0 && (
                    <div className="mb-3">
                      <Form.Label>Issues identified during inspection</Form.Label>
                      <ul className="mb-0 ps-3">
                        {getInspectionIssueItemsFromBooking(bookingData).map(
                          (item, index) => (
                            <li key={`${item.question}-${index}`} className="mb-2">
                              <strong>{item.question}</strong>
                              {item.photos?.length > 0 && (
                                <div className="d-flex flex-wrap gap-2 mt-2">
                                  {item.photos.map((photo, photoIndex) => (
                                    <img
                                      key={`${photo}-${photoIndex}`}
                                      src={formatUploadedAssetUrl(photo)}
                                      alt={item.question}
                                      style={{
                                        width: "80px",
                                        height: "80px",
                                        objectFit: "cover",
                                        borderRadius: "6px",
                                      }}
                                    />
                                  ))}
                                </div>
                              )}
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  )}
                  <Form.Label>Amount to request</Form.Label>
                  <CurrencyInput
                    id="disputeAmount"
                    name="disputeAmount"
                    placeholder="Enter Price"
                    defaultValue={0}
                    decimalsLimit={2}
                    value={disputeAmount}
                    prefix="$"
                    onValueChange={(value) => handleDisputeAmount(value)}
                    className="form-control"
                  />
                  <Form.Label className="mt-3">Description for renter</Form.Label>
                  <TextField
                    style={{ width: "100%" }}
                    multiline
                    rows={4}
                    value={disputeReason}
                    onChange={(event) => setDisputeReason(event.target.value)}
                    label={
                      canDirectChargeFromHold && !claimRequiresRenterApproval
                        ? "Explain the fuel surcharge or cleaning charge"
                        : "Explain the issue and repair costs"
                    }
                    variant="outlined"
                  />
                  <Form.Label className="mt-3">Repair receipts (optional)</Form.Label>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    multiple
                    className="form-control"
                    onChange={async (event) => {
                      try {
                        const uploaded = await uploadDamageClaimReceipts(
                          event.target.files
                        );
                        setDamageClaimReceiptUrls(uploaded);
                      } catch (uploadError) {
                        setError("Failed to upload receipts.");
                      }
                    }}
                  />
                  <p className="text-muted mt-2 mb-0" style={{ fontSize: "13px" }}>
                    {canDirectChargeFromHold && !claimRequiresRenterApproval
                      ? "Enter the amount to capture from the renter's security deposit hold for fuel or cleaning. The remaining hold is released automatically."
                      : "The renter must approve this amount before any charge is processed. Photos and receipts will be shared in messaging."}
                  </p>
                  {error != "" && (
                    <Alert className="mt-3" severity="error">
                      {error}
                    </Alert>
                  )}
                  {success && (
                    <Alert className="mt-3" severity="success">
                      {success}
                    </Alert>
                  )}
                </>
              )}
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDisputeModal}>
            Cancel
          </Button>
          {!isOffPlatformDeposit(paymentDetails) && (
            <>
              {claimRequiresRenterApproval && (
                <Button
                  disabled={
                    !!error ||
                    damageClaimSubmitting ||
                    bookingData?.damageClaim?.status ===
                      "pending_renter_approval"
                  }
                  variant="primary"
                  onClick={submitDamageClaimForApproval}
                >
                  {damageClaimSubmitting
                    ? "Submitting..."
                    : "Send to Renter for Approval"}
                </Button>
              )}
              {canDirectChargeFromHold && !claimRequiresRenterApproval && (
                <Button
                  disabled={
                    paymentProcessing ||
                    !!error ||
                    !disputeAmount ||
                    Number(disputeAmount) <= 0
                  }
                  variant="warning"
                  onClick={() => captureCertainHeldPayment(disputeAmount, true)}
                >
                  {paymentProcessing ? "Processing..." : "Charge"}
                </Button>
              )}
            </>
          )}
        </Modal.Footer>
      </Modal>

      <Modal
        centered
        show={showSendWaiverEmailModal}
        onHide={closeSendWaiverEmailModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>Send Waivers</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col style={{ gap: "5px" }}>
              {/* {disputeDetails ? 
            <>
              <Form.Label>Amount Disputed</Form.Label>
              <p className='mt-0' style={{fontSize: '13px',}}>{disputeDetails.amount ? '$'+disputeDetails.amount : ''}</p>
              <Form.Label>Reason</Form.Label>
              <p className='mt-0' style={{fontSize: '13px',}}>{disputeDetails.reason ? disputeDetails.reason : ''}</p>
            </>
            : ''
            } */}

              <Form.Label>Receiver Email</Form.Label>
              <TextField
                style={{ width: "100%" }}
                id="outlined-basic"
                value={waiverReceiverEmail}
                onChange={(e) => setWaiverReceiverEmail(e.target.value)}
                label="Email"
                variant="outlined"
              />
            </Col>
          </Row>
          <Form.Label className="mt-3" style={{ width: "50%" }}>
            Waiver Type
          </Form.Label>
          <Form.Control
            as="select" // Set the input type to "select"
            name="waiverType"
            style={{ width: "100%" }}
            value={waiverType}
            defaultValue="Adult"
            onChange={(e) => setWaiverType(e.target.value)}
            className="form-control"
            required
          >
            <option default>Select Waiver</option>
            <option value="Adult">Adult Passenger Waiver</option>
            <option value="Minor">Minor Passenger Waiver</option>
            {/* {(bookingData.waiversFilled?.adult < (inquiryData[0].inquiryData.adults + inquiryData[0].inquiryData.seniors)) && 
                    <option value="Adult">Adult</option> }
                  {(bookingData.waiversFilled?.minor < (inquiryData[0].inquiryData.children +  inquiryData[0].inquiryData.infants)) && 
                    <option value="Minor">Minor</option> } */}
          </Form.Control>
          {/* Additional content goes here */}
          <Form.Label className="mt-4" style={{ width: "50%" }}>
            Copy Waivers
          </Form.Label>
          <div className="d-flex gap-3">
            {/* {(bookingData.waiversFilled?.adult < (inquiryData[0].inquiryData.adults + inquiryData[0].inquiryData.seniors)) && 
        <Button onClick={handleCopyAdultWaiver} variant='primary'><FaRegCopy /> Adult Waiver</Button>
          }
        {(bookingData.waiversFilled?.minor < (inquiryData[0].inquiryData.children +  inquiryData[0].inquiryData.infants)) && 
        <Button onClick={handleCopyMinorWaiver} variant='primary'><FaRegCopy /> Minor Waiver</Button>
        } */}
            <Button onClick={handleCopyAdultWaiver} variant="primary">
              <FaRegCopy /> Adult Passenger Waiver
            </Button>
            <Button onClick={handleCopyMinorWaiver} variant="primary">
              <FaRegCopy /> Minor Passenger Waiver
            </Button>
          </div>
          {success && (
            <Alert className="mt-4" severity="success">
              {success}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button disabled={error} variant="primary" onClick={sendWaiverEmail}>
            Send Waiver
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        centered
        show={showPostTripRenterGuidanceModal}
        onHide={closePostTripRenterGuidanceModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {bookingData.damageClaim?.status === "pending_renter_approval"
              ? "Action required: review damage claim"
              : bookingData.damageClaim?.status === "approved"
                ? "Damage claim approved"
                : bookingData.damageClaim?.status === "charged"
                  ? "Damage charge processed"
                  : hasTripInspectionIssue(bookingData)
                    ? "Update on your trip"
                    : "Your trip has ended!"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {bookingData.damageClaim?.status === "pending_renter_approval" ? (
            <>
              <p>
                The boat owner reported{" "}
                {formatRenterInspectionIssuePhrase(bookingData)} and requested a
                charge of $
                {formatCurrency(bookingData.damageClaim.amount)} from your
                security deposit.
              </p>
              <p className="mb-2">{bookingData.damageClaim.reason}</p>
              {bookingData.damageClaim.issueItems?.length > 0 && (
                <ul className="mb-2 ps-3">
                  {bookingData.damageClaim.issueItems.map((item, index) => (
                    <li key={`${item.question}-${index}`} className="mb-2">
                      {item.question}
                      {item.photos?.length > 0 && (
                        <div className="d-flex flex-wrap gap-2 mt-2">
                          {item.photos.map((photo, photoIndex) => (
                            <a
                              key={`${photo}-${photoIndex}`}
                              href={formatUploadedAssetUrl(photo)}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <img
                                src={formatUploadedAssetUrl(photo)}
                                alt={item.question}
                                style={{
                                  width: "80px",
                                  height: "80px",
                                  objectFit: "cover",
                                  borderRadius: "6px",
                                }}
                              />
                            </a>
                          ))}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {bookingData.damageClaim.receiptUrls?.length > 0 && (
                <div className="d-flex flex-wrap gap-2 mb-2">
                  {bookingData.damageClaim.receiptUrls.map((receipt, index) => (
                    <a
                      key={`${receipt}-${index}`}
                      href={formatUploadedAssetUrl(receipt)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      View receipt {index + 1}
                    </a>
                  ))}
                </div>
              )}
              <p className="mb-0 text-muted" style={{ fontSize: "14px" }}>
                Your approval is required before any charge is processed.
              </p>
            </>
          ) : bookingData.damageClaim?.status === "approved" ? (
            <p className="mb-0">
              You approved the $
              {formatCurrency(bookingData.damageClaim.amount)} damage claim.
              The security deposit charge is being processed.
            </p>
          ) : bookingData.damageClaim?.status === "charged" ? (
            <p className="mb-0">
              {formatRenterInspectionChargeLabel(bookingData)} charge of $
              {formatCurrency(
                bookingData.damageCharge || bookingData.damageClaim.amount
              )}{" "}
              was processed from your security deposit.
            </p>
          ) : hasTripInspectionIssue(bookingData) ? (
            <p className="mb-0">
              The boat owner reported{" "}
              {formatRenterInspectionIssuePhrase(bookingData)} and will reach
              out to you through your{" "}
              <Link
                to={`/dashboard/inbox?userId=${bookingData?.owner}`}
                onClick={closePostTripRenterGuidanceModal}
              >
                messaging inbox
              </Link>
              , where you can review the details.
            </p>
          ) : (
            <p className="mb-0">
              Your trip has ended! The boat owner has inspected the vessel and no
              issues were reported. Please leave the boat owner a review and if
              you were fully satisfied with your experience please consider
              leaving a tip for the boat owner. Thank you!
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          {bookingData.damageClaim?.status === "pending_renter_approval" ? (
            <div className="d-flex flex-column gap-2 w-100">
              <Button
                variant="primary"
                disabled={paymentProcessing}
                onClick={handleRenterApproveDamageClaim}
              >
                Approve charge
              </Button>
              <Button
                variant="outline-primary"
                onClick={handleRenterRejectDamageClaim}
              >
                Dispute
              </Button>
              <Link
                to={`/dashboard/inbox?userId=${bookingData.owner}`}
                className="btn btn-link"
                onClick={closePostTripRenterGuidanceModal}
              >
                Message boat owner
              </Link>
            </div>
          ) : hasTripInspectionIssue(bookingData) ? (
            <Button variant="primary" onClick={closePostTripRenterGuidanceModal}>
              Got it
            </Button>
          ) : (
            <div className="d-flex flex-column gap-2 w-100">
              <Button
                variant="link"
                className="p-0 text-start"
                onClick={handlePostTripReviewClick}
              >
                Yes please I&apos;d like to leave a review
              </Button>
              <Button
                variant="link"
                className="p-0 text-start"
                onClick={handlePostTripDecline}
              >
                Decline
              </Button>
            </div>
          )}
        </Modal.Footer>
      </Modal>

      <Modal
        centered
        show={showAllGoodConfirmationModal}
        onHide={closeAllGoodConfirmationModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>All Good!</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            You&apos;ve marked the inspection as All Good. If you clicked by
            mistake or find an issue or fuel surcharge after submitting, you can
            go back and update your inspection.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleAllGoodGoBack}>
            Go Back
          </Button>
          <Button variant="primary" onClick={closeAllGoodConfirmationModal}>
            Got it
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        centered
        show={showTripStartedGuidanceModal}
        onHide={closeTripStartedGuidanceModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>Trip Started!</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">Have fun and be safe!</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={closeTripStartedGuidanceModal}>
            Got it
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        centered
        show={showCaptainStartTripGuidanceModal}
        onHide={() => closeCaptainStartTripGuidanceModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Time to head out!</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            {getCaptainStartTripGuidanceMessage(boatDetails)}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={() => closeCaptainStartTripGuidanceModal(true)}
          >
            Got it
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        size="md"
        centered
        show={startTripModal}
        onHide={closeStartTripModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>Start Your Trip</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Alert
              severity={
                arePassengerWaiversComplete(bookingForWaivers, boatDetails)
                  ? "success"
                  : "warning"
              }
            >
              Required Passenger Waivers Filled{" "}
              {getFilledPassengerWaivers(bookingData)} out of{" "}
              {getRequiredPassengerWaivers(bookingForWaivers, boatDetails)}
            </Alert>

            {passengerNames.length > 0 && (
              <div className="mt-3">
                <h6 style={{ fontWeight: "bold" }}>Passengers on Board ({passengerNames.length}):</h6>
                <ol style={{ paddingLeft: "20px", marginBottom: "0" }}>
                  {passengerNames.map((passenger, index) => (
                    <li key={index} style={{ marginBottom: "4px" }}>
                      {passenger.name}{" "}
                      <span style={{ fontSize: "12px", color: "#888" }}>
                        ({passenger.type})
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {!arePassengerWaiversComplete(bookingForWaivers, boatDetails) && (
                <>
                  <Alert severity="error" className="mt-3">
                    Hey Captain ... check all passengers have completed a waiver before you start the trip
                  </Alert>
                  <div className="mt-3 d-flex gap-2 flex-wrap justify-content-center">
                    <Link
                      to={"/waivers/adultWaiver/" + dynamicId}
                      state={{ returnToBooking: true }}
                      className="btn btn-primary btn-sm"
                    >
                      Adult Passenger Waiver
                    </Link>
                    <Link
                      to={"/waivers/minorWaiver/" + dynamicId}
                      state={{ returnToBooking: true }}
                      className="btn btn-primary btn-sm"
                    >
                      Minor Passenger Waiver
                    </Link>
                    <Button
                      className="send-waiver-button btn-sm"
                      onClick={() => { closeStartTripModal(); openSendWaiverEmailModal(); }}
                    >
                      <RiMailSendLine /> Send Waivers
                    </Button>
                  </div>
                </>
              )}

            {arePassengerWaiversComplete(bookingForWaivers, boatDetails) && (
                <h6 className="mt-3">
                  {userData?.role === "Admin"
                    ? "As admin, the trip will start immediately without the captain waiver, equipment checklist, or credit card hold. Confirm the renter has paid the security deposit to the boat owner."
                    : "Are you sure you want to start trip?"}
                </h6>
              )}
          </Row>
        </Modal.Body>
        <Modal.Footer>
          {startTripLoading ? (
            <Button className="btn btn-warning" disabled>
              <ThreeDots
                visible={true}
                height="22"
                width="22"
                color="#fff"
                radius="20"
                ariaLabel="three-dots-loading"
              />
            </Button>
          ) : (
            <Button
              className="btn btn-warning"
              onClick={handleStartTrip}
            >
              Start Trip
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <Modal
        size="md"
        centered
        show={cancelBookingModal}
        onHide={closeCancelBookingModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>Cancel Advanced Booking</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <h6 className="mt-3">
              Are you sure you want to cancel your booking?
            </h6>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          {cancelBookingLoading ? (
            <Button className="btn btn-warning">
              <ThreeDots
                visible={true}
                height="22"
                width="22"
                color="#fff"
                radius="20"
                ariaLabel="three-dots-loading"
                wrapperStyle={{}}
                wrapperClass=""
              />
            </Button>
          ) : (
            <Button className="btn btn-warning" onClick={handleCancelBooking}>
              Cancel Booking
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <Modal centered show={removeModal} onHide={closeRemoveOfferModal}>
        <Modal.Header closeButton>
          <Modal.Title>Permanently Remove</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to remove this booking?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeRemoveOfferModal}>
            Close
          </Button>
          {removeBookingLoading ? (
            <Button variant="warning">
              <ThreeDots
                visible={true}
                height="22"
                width="22"
                color="#fff"
                radius="20"
                ariaLabel="three-dots-loading"
                wrapperStyle={{}}
                wrapperClass=""
              />
            </Button>
          ) : (
            <Button
              variant="warning"
              className="d-flex align-items-center gap-1"
              onClick={handleRemoveOffer}
            >
              <RiDeleteBin6Line size={15} /> <span>Remove</span>
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Edit Booking Modal */}
      {/* Edit Booking Modal */}
      <EditBookingModal
        show={editBookingModalOpen}
        onHide={() => setEditBookingModalOpen(false)}
        bookingData={bookingData}
        inquiryData={inquiryData}
        boatDetails={boatDetails}
        paymentDetails={paymentDetails}
        currentUser={currentUser}
        onUpdateSuccess={() => setTimeout(() => window.location.reload(), 2000)}
      />

      {/* Waiver View Modal (Admin Only) */}
      <Modal
        show={showWaiverModal}
        onHide={() => { setShowWaiverModal(false); setSelectedWaiver(null); setWaiverDescription(""); }}
        size="lg"
        centered
        dialogClassName="waiver-document-modal"
      >
        <Modal.Header closeButton style={{ background: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
          <Modal.Title>
            {selectedWaiver?.type} Passenger Waiver
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#2c2c2c", padding: "24px" }}>
          {selectedWaiver && (
            <div id="waiver-print-content">
              {/* Header Card with Logo */}
              <div style={{ background: "#fff", borderRadius: "8px", padding: "32px", display: "flex", alignItems: "center", marginBottom: "20px" }}>
                <div style={{ flex: "0 0 30%" }}>
                  <img
                    src="/assets/backend/images/logo.png"
                    alt="BBR Logo"
                    style={{ width: "100%", maxWidth: "180px", backgroundColor: "#121212", padding: "8px", borderRadius: "4px" }}
                  />
                </div>
                <div style={{ flex: "1", textAlign: "center" }}>
                  <h3 style={{ color: "#27313F", fontWeight: "800", margin: "0 0 4px 0" }}>BBR BASS BOAT RENTALS, LLC</h3>
                  <p style={{ color: "#27313F", fontWeight: "400", fontSize: "16px", margin: 0 }}>
                    {selectedWaiver.type.toUpperCase()} PASSENGER WAIVER
                  </p>
                </div>
              </div>

              {/* Waiver Description / Legal Text */}
              {waiverDescLoading ? (
                <div style={{ background: "#fff", borderRadius: "8px", padding: "24px", marginBottom: "20px", textAlign: "center" }}>
                  <p>Loading waiver document...</p>
                </div>
              ) : waiverDescription ? (
                <div style={{ background: "#fff", borderRadius: "8px", padding: "32px", marginBottom: "20px" }}>
                  <h2 style={{ fontSize: "24px", fontWeight: "700", marginBottom: "16px" }}>
                    {selectedWaiver.type} Passenger Waiver
                  </h2>
                  <div
                    className="waiverDescrption"
                    dangerouslySetInnerHTML={{ __html: waiverDescription }}
                    style={{ fontSize: "13px", lineHeight: "1.6", color: "#333" }}
                  />
                </div>
              ) : null}

              {/* Filled Waiver Data */}
              <div style={{ background: "#fff", borderRadius: "8px", padding: "32px" }}>
                <h3 style={{ fontSize: "20px", fontWeight: "700", marginBottom: "8px" }}>
                  Waiver Responses — {selectedWaiver.name}
                </h3>
                {selectedWaiver.signedAt && (
                  <p style={{ fontSize: "13px", color: "#555", marginBottom: "20px", display: "flex", alignItems: "center", gap: "6px" }}>
                    <span style={{ fontSize: "16px" }}>✅</span>
                    <strong>Signed on:</strong>&nbsp;
                    {new Date(selectedWaiver.signedAt).toLocaleString("en-US", {
                      weekday: "long",
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                      hour: "numeric",
                      minute: "numeric",
                      hour12: true,
                    })}
                  </p>
                )}
                {selectedWaiver.waiverData.map((item, idx) => {
                  const questionLower = (item.question || "").toLowerCase();
                  const isSignature = questionLower.includes("signature");
                  const isImageUrl = typeof item.answer === "string" && (item.answer.startsWith("http") && (item.answer.endsWith(".png") || item.answer.endsWith(".jpg") || item.answer.endsWith(".jpeg")));
                  const isDataUri = typeof item.answer === "string" && item.answer.startsWith("data:image");
                  const isImageArray = Array.isArray(item.answer) && item.answer.length > 0 && item.answer[0]?.url;

                  return (
                    <div key={idx} style={{ marginBottom: "16px" }}>
                      <label style={{ fontWeight: "600", fontSize: "14px", color: "#333", display: "block", marginBottom: "6px" }}>
                        {item.question}:
                      </label>
                      {(isSignature && (isImageUrl || isDataUri)) || isDataUri ? (
                        <div style={{ border: "1px solid #ddd", borderRadius: "6px", padding: "12px", background: "#fafafa", display: "inline-block" }}>
                          <img
                            src={item.answer}
                            alt="Signature"
                            style={{ maxWidth: "350px", maxHeight: "120px" }}
                          />
                        </div>
                      ) : isImageUrl ? (
                        <div style={{ border: "1px solid #ddd", borderRadius: "6px", padding: "12px", background: "#fafafa", display: "inline-block" }}>
                          <img
                            src={item.answer}
                            alt={item.question}
                            style={{ maxWidth: "350px", maxHeight: "150px" }}
                          />
                        </div>
                      ) : isImageArray ? (
                        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                          {item.answer.map((img, imgIdx) => (
                            <div key={imgIdx} style={{ border: "1px solid #ddd", borderRadius: "6px", padding: "8px", background: "#fafafa" }}>
                              <img
                                src={img.url}
                                alt={`Upload ${imgIdx + 1}`}
                                style={{ maxWidth: "120px", maxHeight: "120px" }}
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <input
                          type="text"
                          className="form-control"
                          value={item.answer || "—"}
                          readOnly
                          disabled
                          style={{ backgroundColor: "#f5f5f5" }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ background: "#f8f9fa" }}>
          <Button variant="secondary" onClick={() => { setShowWaiverModal(false); setSelectedWaiver(null); setWaiverDescription(""); }}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              const printContent = document.getElementById("waiver-print-content");
              if (!printContent) return;
              const printWindow = window.open("", "_blank");
              printWindow.document.write(`
                <html>
                  <head>
                    <title>Waiver - ${selectedWaiver?.name}</title>
                    <style>
                      body { font-family: Arial, sans-serif; padding: 30px; color: #333; background: #fff; }
                      h2, h3 { margin-top: 0; }
                      .waiverDescrption { font-size: 13px; line-height: 1.6; }
                      .waiverDescrption p { margin-bottom: 8px; }
                      label { font-weight: 600; font-size: 14px; display: block; margin-bottom: 6px; color: #333; }
                      input[type="text"] { width: 100%; padding: 8px 12px; border: 1px solid #ddd; border-radius: 4px; background: #f5f5f5; font-size: 14px; color: #333; box-sizing: border-box; }
                      img { max-width: 350px; max-height: 120px; }
                      div[style*="border-radius: 8px"] { border-radius: 0 !important; box-shadow: none !important; }
                      @media print {
                        body { padding: 15px; }
                        div[style*="background: rgb(44, 44, 44)"] { background: #fff !important; padding: 0 !important; }
                      }
                    </style>
                  </head>
                  <body>${printContent.innerHTML}</body>
                </html>
              `);
              printWindow.document.close();
              printWindow.focus();
              setTimeout(() => printWindow.print(), 500);
            }}
          >
            Print / Save as PDF
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
